import { useContext } from 'react';
import { MeContext } from '@/app/MeContext';
import { useRecordPost } from './RecordContext';
import type { ChatContext as ContractChatContext, ProfileRuleKind, ManualRecordInput, RecordCandidate, ManualCommunicationInput, ManualCommunicationRecord, CommunicationCapability, CommunicationImportInput, CommunicationOutcomeInput, CommunicationTaskInput, StrangerPriorityOverrideInput, StrangerResearchQueueInput, StrangerTriagePolicyInput, CompanyBackfillInput, CompanyEnrichmentDecisionInput, CompanyEnrichmentProposalInput, CompanyGroupLinkInput, CompanyRelationshipRoleInput, GcpCostSnapshotInput, SourceRefreshInput } from '@norum/contracts';
import type { CanonicalRecord, ConsolidationEvent, ConsolidationHistoryPage, DuplicatePage, DuplicatePair, IdentityAutomationPolicy, SubjectLinkAutomationPolicy } from '@norum/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { TimelineItemKind } from '@norum/contracts';
import { appPath, mcpRead, post } from './client';
import { admin, adminPath } from './admin';
import { fetchMe, switchDepartment, type Me } from './session';
import type {
  WirePlatformHealth,
  WireOperation,
  WireOperationDetail,
  WireOperationsCatalogue,
  WireNightlyAudit,
  WireEngineeringBrief,
  WireStanding,
  WireHostedClient,
  WireShortlistRow,
  WireShortlistFull,
  WireAutoconfirmRecord,
  WireEvidenceProposal,
  WireStrategy,
  ActivityRow,
  WireRoom,
  ProspectStage,
  TermLevel,
  WireConnections,
  WireVendors,
  WireProspect,
  WireCampaign,
  WireCampaignDetail,
  WireMailing,
  WireMailingDetail,
  WireMailingRequest,
  WireQueuedTouch,
  WireLifecycleContact,
  WireLifecycleContacts,
  WireOutreachDigest,
  LifecycleBasis,
  LifecycleEvidence,
  WireLedgerProposal,
  WireLedgerProposalDetail,
  WireIncident,
  WireFolder,
  WireInvitation,
  WireErasure,
  WireMoved,
  WirePerson,
  WirePersonDetail,
  WireNotification,
  PublicationChannel,
  WireProject,
  WireProjectDetail,
  WireMergeProposal,
  WireChange,
  WireChangeDetail,
  ClaimResult,
  DecideResult,
  EventsResponse,
  IncomingResponse,
  Relationship,
  ThreadReadResponse,
  TodayResponse,
  WireCompany,
  WireContact,
  WireDraft,
  WireEmployment,
  WireFlexEntity,
  WireFlexRow,
  WireHeld,
  WireHeldSender,
  WireRequest,
  WireRouting,
  WireSubject,
  TurnResult,
  WireAvailabilityRule,
  WireBrief,
  WireChatSession,
  WireChatMaterial,
  WireChatTurn,
  WireMailbox,
  WireSlot,
  WireTask,
  WireTaskNote,
  WireRecordElaboration,
  WireThread,
  WireAgent,
  WireAssignment,
  WireAuditRow,
  WireChain,
  WireControls,
  WireCostSummary,
  WireDepartment,
  WireDepartmentCreated,
  WireDepartmentDetail,
  WireMinted,
  WireQueueItem,
  WireTool,
  WireAttachedTool,
  WireFiledDocument,
  WireMarket,
  WireMarketRow,
  WirePlan,
  WireObjectionCount,
  WireDatedRate,
  WireProfileSuggestion,
  WireResearchStep,
  WireProfile,
  WireProspectRecord,
  WireProfileRule,
  WireIndexEntry,
  WireIntro,
  WireProfileChange,
  WireSample,
  WireBuyer,
  WireOffer,
  WireOfferRow,
  WireSupplier,
  WireSupplierRow,
  WireSignal,
  WireMarketSource,
  WireTerritory,
  WireTrigger,
  WireViewer,
  AutonomyDimension,
  AutonomyLevel,
  WireExperienceLine,
  WireExperiencePayload,
  WireExperienceVersion,
  WirePromotion,
  WireRule,
  WirePreference,
  WireToolset,
  WireToken,
  WireDecisionRow,
  RuleKind,
  MarketsResponse,
  WireSendAuthority,
  WireSuppressionCounts,
  WireReadiness,
  WireSetup,
  WireWorkspaceItem,
  WireTaskTools,
  WireTwinHandoff,
  WireTwinPlanAnswer,
  WireTwinTask,
  WireTwinTaskConfirmed,
  WireTwinTaskRow,
} from './wire';
import type { Span } from '@/today/grid';

/**
 * Components never fetch. These hooks are the only callers of `post`, so
 * what the interface asks the platform for is readable in one file — and
 * every path in it is on the allow-list, checked at module load.
 */

const P = {
  companyWorkspace: appPath('/v1/companies/workspace'),
  companyOperations: appPath('/v1/companies/operations'),
  companyRoleRecord: appPath('/v1/companies/roles/record'),
  companyGroupLink: appPath('/v1/companies/groups/link'),
  companyEnrichmentPropose: appPath('/v1/companies/enrichment/propose'),
  companyEnrichmentDecide: appPath('/v1/companies/enrichment/decide'),
  companySourceRefresh: appPath('/v1/companies/sources/refresh'),
  companyBackfill: appPath('/v1/companies/backfill'),
  gcpCosts: appPath('/v1/platform/gcp-costs'),
  gcpCostsRecord: appPath('/v1/platform/gcp-costs/record'),
  manualCommunications: appPath('/v1/communications/manual/list'),
  manualCommunicationCreate: appPath('/v1/communications/manual/create'),
  communicationCapabilities: appPath('/v1/communications/capabilities'),
  communicationImport: appPath('/v1/communications/connections/import'),
  communicationTaskCreate: appPath('/v1/communications/tasks/create'),
  communicationOutcome: appPath('/v1/communications/manual/outcomes'),
  manualCommunicationAttachmentUpload: appPath('/v1/communications/manual/attachments/upload'),
  manualCommunicationAttachmentDownload: appPath('/v1/communications/manual/attachments/download'),
  today: appPath('/v1/today'),
  place: appPath('/v1/today/place'),
  undo: appPath('/v1/today/undo'),
  arrive: appPath('/v1/today/arrive'),
  recap: appPath('/v1/today/recap'),
  events: appPath('/v1/calendar/events'),
  slots: appPath('/v1/calendar/slots'),
  availabilityGet: appPath('/v1/availability/get'),
  availabilitySet: appPath('/v1/availability/set'),
  threads: appPath('/v1/threads/list'),
  threadRead: appPath('/v1/threads/read'),
  tasks: appPath('/v1/tasks/list'),
  taskClose: appPath('/v1/tasks/close'),
  task: appPath('/v1/tasks/get'),
  taskBoard: appPath('/v1/tasks/board'),
  taskUpdate: appPath('/v1/tasks/update'),
  taskCreate: appPath('/v1/tasks/create'),
  taskAssign: appPath('/v1/tasks/assign'),
  taskAssignees: appPath('/v1/tasks/assignees'),
  taskRemove: appPath('/v1/tasks/remove'),
  threadRemove: appPath('/v1/threads/remove'),
  mailRules: appPath('/v1/mail-rules/list'),
  mailRuleAdd: appPath('/v1/mail-rules/add'),
  mailRuleRevoke: appPath('/v1/mail-rules/revoke'),
  removalDecline: appPath('/v1/removals/decline'),
  directions: appPath('/v1/directions/list'),
  directionAdd: appPath('/v1/directions/add'),
  directionRevoke: appPath('/v1/directions/revoke'),
  awayList: appPath('/v1/away/list'),
  awaySet: appPath('/v1/away/set'),
  awayEnd: appPath('/v1/away/end'),
  planRests: appPath('/v1/plans/rests'),
  taskApproveHeld: appPath('/v1/tasks/approve-held'),
  // What an assistant proposed, for a person to confirm (ADR-0153).
  shortlists: appPath('/v1/shortlists/list'),
  shortlist: appPath('/v1/shortlists/get'),
  decideShortlist: appPath('/v1/shortlists/decide'),
  evidenceProposals: appPath('/v1/evidence/proposals'),
  decideEvidence: appPath('/v1/evidence/decide'),
  strategies: appPath('/v1/strategies/list'),
  decideStrategy: appPath('/v1/strategies/decide'),
  taskNotes: appPath('/v1/tasks/notes/list'),
  taskNoteAdd: appPath('/v1/tasks/notes/add'),
  taskElaborate: appPath('/v1/tasks/elaborate'),
  recordElaborate: appPath('/v1/records/elaborate'),
  recordElaborations: appPath('/v1/records/elaborations'),
  researchUsesFind: appPath('/v1/requests/uses/find'),
  researchUses: appPath('/v1/requests/uses'),
  researchUsesDecide: appPath('/v1/requests/uses/decide'),
  taskAdd: appPath('/v1/tasks/add'),
  taskSchedule: appPath('/v1/tasks/schedule'),
  mailboxes: appPath('/v1/mailboxes/list'),
  briefLive: appPath('/v1/briefs/live'),
  briefCompose: appPath('/v1/briefs/compose'),
  draftCompose: appPath('/v1/drafts/compose'),
  chatOpen: appPath('/v1/chat/open'),
  chatSay: appPath('/v1/chat/say'),
  chatRead: appPath('/v1/chat/read'),
  chatClose: appPath('/v1/chat/close'),
  chatAgents: appPath('/v1/chat/agents'),
  chatTask: appPath('/v1/chat/task'),
  conversations: appPath('/v1/conversations/list'),
  conversationOpen: appPath('/v1/conversations/open'),
  conversationRead: appPath('/v1/conversations/read'),
  conversationSend: appPath('/v1/conversations/send'),
  conversationDraft: appPath('/v1/conversations/draft'),
  // S2
  threadGet: appPath('/v1/threads/get'),
  claim: appPath('/v1/threads/claim'),
  unclaim: appPath('/v1/threads/unclaim'),
  confirmLink: appPath('/v1/subjects/confirm'),
  linkReviews: appPath('/v1/subjects/reviews'),
  linkDecide: appPath('/v1/subjects/decide'),
  drafts: appPath('/v1/drafts/list'),
  decide: appPath('/v1/drafts/decide'),
  redraft: appPath('/v1/drafts/redraft'),
  guidanceList: appPath('/v1/guidance/list'),
  guidanceAdd: appPath('/v1/guidance/add'),
  guidanceDecide: appPath('/v1/guidance/decide'),
  guidanceRevoke: appPath('/v1/guidance/revoke'),
  held: appPath('/v1/unresolved/list'),
  strangerPolicyEnable: appPath('/v1/unresolved/research/policies/enable'),
  strangerPolicyStop: appPath('/v1/unresolved/research/policies/stop'),
  strangerResearchQueue: appPath('/v1/unresolved/research/queue'),
  strangerPriority: appPath('/v1/unresolved/research/priority'),
  promote: appPath('/v1/unresolved/promote'),
  knowAndApprove: appPath('/v1/unresolved/know-and-approve'),
  dismiss: appPath('/v1/unresolved/dismiss'),
  digestTime: appPath('/v1/unresolved/digest-time'),
  directoryList: appPath('/v1/directory/list'),
  directoryGet: appPath('/v1/directory/get'),
  contactAssign: appPath('/v1/contacts/assign'),
  contactUpdate: appPath('/v1/contacts/update'),
  companyUpdate: appPath('/v1/companies/update'),
  move: appPath('/v1/employments/move'),
  approve: appPath('/v1/counterparties/approve'),
  incoming: appPath('/v1/records/incoming'),
  flexEntities: appPath('/v1/flex/entities'),
  pipeline: appPath('/v1/insight/pipeline'),
  flexList: appPath('/v1/flex/list'),
  flexGet: appPath('/v1/flex/get'),
  flexWrite: appPath('/v1/flex/write'),
  // S3
  requests: appPath('/v1/requests/list'),
  requestRead: appPath('/v1/requests/read'),
  requestStanding: appPath('/v1/requests/standing'),
  taskStanding: appPath('/v1/tasks/standing'),
  platformHealth: appPath('/v1/platform/health'),
  // A superadmin's twin keeping the platform (Amendment 0013, ADR-0172).
  operationsCatalogue: appPath('/v1/operations/catalogue'),
  operationsList: appPath('/v1/operations/list'),
  operationRead: appPath('/v1/operations/read'),
  operationRun: appPath('/v1/operations/run'),
  operationsStanding: appPath('/v1/operations/standing'),
  operationsStandingStop: appPath('/v1/operations/standing/stop'),
  nightly: appPath('/v1/operations/nightly'),
  nightlyRead: appPath('/v1/operations/nightly/read'),
  briefs: appPath('/v1/briefs/list'),
  briefRead: appPath('/v1/briefs/read'),
  requestOpen: appPath('/v1/requests/open'),
  requestComplete: appPath('/v1/requests/complete'),
  requestRefuse: appPath('/v1/requests/refuse'),
  requestCancel: appPath('/v1/requests/cancel'),
  requestAcknowledge: appPath('/v1/requests/acknowledge'),
  suggestionOpen: appPath('/v1/suggestions/open'),
  activity: appPath('/v1/reports/activity'),
  // S4: oversight, read.
  approvals: appPath('/v1/approvals/queue'),
  agents: appPath('/v1/agents/list'),
  agent: appPath('/v1/agents/get'),
  assignments: appPath('/v1/assignments/list'),
  departments: appPath('/v1/departments/list'),
  department: appPath('/v1/departments/get'),
  tools: appPath('/v1/tools/list'),
  toolAttachments: appPath('/v1/tools/attachments'),
  toolWhere: appPath('/v1/tools/where'),
  marketList: appPath('/v1/markets/list'),
  marketGet: appPath('/v1/markets/get'),
  marketSources: appPath('/v1/markets/sources'),
  marketScreen: appPath('/v1/markets/screen'),
  marketDecide: appPath('/v1/markets/decide'),
  marketConfirmRoute: appPath('/v1/markets/confirm-route'),
  marketResearch: appPath('/v1/markets/research'),
  distributorResearch: appPath('/v1/distributors/research'),
  marketReport: appPath('/v1/markets/report'),
  documentCreate: appPath('/v1/documents/create'),
  documentList: appPath('/v1/documents/list'),
  mapsKey: appPath('/v1/maps/key'),
  profiles: appPath('/v1/profiles/list'),
  profileLive: appPath('/v1/profiles/live'),
  territories: appPath('/v1/territories/list'),
  territory: appPath('/v1/territories/get'),
  prospectingPlans: appPath('/v1/plans/list'),
  planDryRun: appPath('/v1/plans/dry-run'),
  signals: appPath('/v1/signals/list'),
  prospectLost: appPath('/v1/prospects/lost'),
  prospectObjection: appPath('/v1/prospects/objection'),
  profileSuggest: appPath('/v1/profiles/suggest'),
  prospectApproveCompany: appPath('/v1/prospects/approve-company'),
  prospectIntro: appPath('/v1/prospects/intro'),
  introAnswer: appPath('/v1/intros/answer'),
  prospectSample: appPath('/v1/prospects/sample'),
  prospectOptOut: appPath('/v1/prospects/opt-out'),
  prospectDetails: appPath('/v1/prospects/details'),
  companyDetails: appPath('/v1/companies/details'),
  prospectErase: appPath('/v1/prospects/erase'),
  prospectingSetup: appPath('/v1/prospecting/setup'),
  profileChanges: appPath('/v1/profile-changes/list'),
  profileChangeDecide: appPath('/v1/profile-changes/decide'),
  intros: appPath('/v1/intros/list'),
  samples: appPath('/v1/samples/list'),
  sampleOutcome: appPath('/v1/samples/outcome'),
  ourIndex: appPath('/v1/prospecting/index'),
  marketEvidence: appPath('/v1/markets/evidence'),
  buyers: appPath('/v1/buyers/list'),
  buyerCreate: appPath('/v1/buyers/create'),
  buyerApprove: appPath('/v1/buyers/approve'),
  suppliers: appPath('/v1/suppliers/list'),
  supplierCreate: appPath('/v1/suppliers/create'),
  supplier: appPath('/v1/suppliers/get'),
  supplierAnswer: appPath('/v1/suppliers/answer'),
  supplierConfirm: appPath('/v1/suppliers/confirm'),
  supplierAsk: appPath('/v1/suppliers/ask'),
  supplierAsked: appPath('/v1/suppliers/asked'),
  supplierContact: appPath('/v1/suppliers/contact'),
  supplierFindContact: appPath('/v1/suppliers/find-contact'),
  offers: appPath('/v1/offers/list'),
  offerCreate: appPath('/v1/offers/create'),
  offer: appPath('/v1/offers/get'),
  offerPrepare: appPath('/v1/offers/prepare'),
  offerSent: appPath('/v1/offers/sent'),
  offerSendTo: appPath('/v1/offers/send-to'),
  offerRecipientSent: appPath('/v1/offers/recipient-sent'),
  prospectingReport: appPath('/v1/reports/prospecting'),
  morningBatch: appPath('/v1/prospecting/batch'),
  audit: appPath('/v1/audit/list'),
  twinTrail: appPath('/v1/audit/twin'),
  auditVerify: appPath('/v1/audit/verify'),
  cost: appPath('/v1/cost/summary'),
  triggers: appPath('/v1/rollback/triggers'),
  // S7: what happened.
  notifications: appPath('/v1/notifications/list'),
  notificationsRead: appPath('/v1/notifications/read'),
  incidents: appPath('/v1/incidents/list'),
  incident: appPath('/v1/incidents/get'),
  acknowledgeIncident: appPath('/v1/incidents/acknowledge'),
  ledgerProposals: appPath('/v1/ledger/proposals'),
  ledgerProposal: appPath('/v1/ledger/proposal'),
  decideLedgerProposal: appPath('/v1/ledger/proposals/decide'),
  declineMirrorsOwn: appPath('/v1/ledger/proposals/decline-mirrors-own'),
  // S8: people, and the drive's folders by key.
  people: appPath('/v1/people/list'),
  person: appPath('/v1/people/get'),
  driveFolders: appPath('/v1/drive/folders'),
  driveFiles: appPath('/v1/drive/files'),
  capture: appPath('/v1/calendar/capture'),
  commitments: appPath('/v1/commitments/list'),
  confirmCommitments: appPath('/v1/commitments/confirm'),
  dismissCommitment: appPath('/v1/commitments/dismiss'),
  invitations: appPath('/v1/people/invitations'),
  erasures: appPath('/v1/erasure/list'),
  erasure: appPath('/v1/erasure/get'),
  requestErasure: appPath('/v1/erasure/request'),
  // S5: make it mine, put it back.
  experienceLine: appPath('/v1/experience/line'),
  personalise: appPath('/v1/experience/personalise'),
  experienceReset: appPath('/v1/experience/reset'),
  promotions: appPath('/v1/promotions/list'),
  propose: appPath('/v1/promotions/propose'),
  decidePromotion: appPath('/v1/promotions/decide'),
  rules: appPath('/v1/rules/list'),
  ruleAdd: appPath('/v1/rules/add'),
  ruleRevoke: appPath('/v1/rules/revoke'),
  preferences: appPath('/v1/preferences/list'),
  preferenceAdd: appPath('/v1/preferences/add'),
  preferenceRevoke: appPath('/v1/preferences/revoke'),
  toolsets: appPath('/v1/toolsets/list'),
  toolsetsMine: appPath('/v1/toolsets/mine'),
  selfSelect: appPath('/v1/toolsets/self-select'),
  defineToolset: appPath('/v1/toolsets/define'),
  tokensMint: appPath('/v1/tokens/mint'),
  tokensList: appPath('/v1/tokens/list'),
  tokensRevoke: appPath('/v1/tokens/revoke'),
  constitution: appPath('/v1/ledger/constitution'),
  decisions: appPath('/v1/ledger/decisions'),
  decision: appPath('/v1/ledger/decision'),
  // S6: the change loop.
  changes: appPath('/v1/loop/list'),
  change: appPath('/v1/loop/status'),
  changePreview: appPath('/v1/loop/preview'),
  changeStart: appPath('/v1/loop/start'),
  changeConfirm: appPath('/v1/loop/confirm'),
  changeAbandon: appPath('/v1/loop/abandon'),
  buildApprove: appPath('/v1/builds/approve'),
  buildReturn: appPath('/v1/builds/return'),
  merges: appPath('/v1/merges/list'),
  mergeDecide: appPath('/v1/merges/decide'),
  projects: appPath('/v1/projects/list'),
  project: appPath('/v1/projects/get'),
  projectRoom: appPath('/v1/projects/room'),
  projectSay: appPath('/v1/projects/room/say'),
  projectConvene: appPath('/v1/projects/meetings/convene'),
  projectCreate: appPath('/v1/projects/create'),
  projectAddMember: appPath('/v1/projects/add-member'),
  projectDraft: appPath('/v1/projects/draft'),
  publicationDecide: appPath('/v1/publications/decide'),
  calendarHold: appPath('/v1/calendar/hold'),
  calendarInvite: appPath('/v1/calendar/invite'),
  calendarMove: appPath('/v1/calendar/move'),
  calendarCancel: appPath('/v1/calendar/cancel'),
  calendarFollowing: appPath('/v1/calendar/following'),
  calendarRelease: appPath('/v1/calendar/release'),
  // Outbound outreach, read (proposal 0007).
  authority: appPath('/v1/drafts/authority'),
  markets: appPath('/v1/outreach/markets'),
  suppression: appPath('/v1/outreach/suppression'),
  suppressionCheck: appPath('/v1/outreach/suppression/check'),
  liftRequests: appPath('/v1/outreach/suppression/lift-requests'),
  liftRequest: appPath('/v1/outreach/suppression/lift-request'),
  discover: appPath('/v1/outreach/discover'),
  prospects: appPath('/v1/outreach/prospects'),
  prospect: appPath('/v1/outreach/prospect'),
  excludeProspect: appPath('/v1/outreach/prospect/exclude'),
  draftProspect: appPath('/v1/outreach/prospect/draft'),
  retryProspect: appPath('/v1/outreach/prospect/retry'),
  importProspects: appPath('/v1/outreach/prospects/import'),
  // Campaigns (ADR-0079, ADR-0086). Approval is answered by core-api.
  campaigns: appPath('/v1/outreach/campaigns'),
  sequences: appPath('/v1/outreach/sequences'),
  createSequence: appPath('/v1/outreach/sequences/create'),
  approveSequence: appPath('/v1/outreach/sequences/approve'),
  retireSequence: appPath('/v1/outreach/sequences/retire'),
  covers: appPath('/v1/cover/list'),
  setCover: appPath('/v1/cover/set'),
  endCover: appPath('/v1/cover/end'),
  onboarding: appPath('/v1/people/onboarding'),
  attachments: appPath('/v1/attachments/list'),
  uploadAttachment: appPath('/v1/attachments/upload'),
  driveAttachment: appPath('/v1/attachments/drive'),
  bookingLinks: appPath('/v1/booking-links'),
  createBookingLink: appPath('/v1/booking-links/create'),
  retireBookingLink: appPath('/v1/booking-links/retire'),
  decideBooking: appPath('/v1/bookings/decide'),
  campaign: appPath('/v1/outreach/campaign'),
  createCampaign: appPath('/v1/outreach/campaigns/create'),
  approveCampaign: appPath('/v1/outreach/campaigns/approve'),
  endCampaign: appPath('/v1/outreach/campaign/end'),
  outreachQueue: appPath('/v1/outreach/queue'),
  // Mailings a journey may ask for (ADR-0099). Approval is answered by core-api.
  mailings: appPath('/v1/mailings'),
  mailing: appPath('/v1/mailing'),
  mailingRequests: appPath('/v1/mailing/requests'),
  createMailing: appPath('/v1/mailings/create'),
  approveMailing: appPath('/v1/mailings/approve'),
  endMailing: appPath('/v1/mailing/end'),
  complaint: appPath('/v1/outreach/complaint'),
  // Lifecycle (ADR-0082, ADR-0087) and the weekly report (FR-OUT-76).
  lifecycleContacts: appPath('/v1/lifecycle/contacts'),
  lifecycleContact: appPath('/v1/lifecycle/contact'),
  lifecycleRecord: appPath('/v1/lifecycle/record'),
  lifecycleWithdraw: appPath('/v1/lifecycle/withdraw'),
  lifecycleImport: appPath('/v1/lifecycle/import'),
  lifecycleSync: appPath('/v1/lifecycle/sync'),
  lifecycleTrack: appPath('/v1/lifecycle/track'),
  outreachReport: appPath('/v1/outreach/report'),
  // The Workspace (ADR-0117).
  workspaceQueue: appPath('/v1/workspace/queue'),
  // A twin's task (Amendment 0011, ADR-0139): planned, confirmed, read back.
  chatMaterialList: appPath('/v1/chat/material/list'),
  chatMaterialUpload: appPath('/v1/chat/material/upload'),
  chatMaterialDrive: appPath('/v1/chat/material/drive'),
  twinPlan: appPath('/v1/chat/twin-plan'),
  twinPlanLatest: appPath('/v1/chat/twin-plan/latest'),
  twinTask: appPath('/v1/chat/twin-task'),
  twinHandoff: appPath('/v1/chat/twin-handoff'),
  twinTasks: appPath('/v1/tasks/twin/list'),
  twinTaskRead: appPath('/v1/tasks/twin/read'),
};

/** The admin door's controls (ADR-0073). Superadmin only, cookie only. */
const A = {
  mint: adminPath('/v1/supervision/mint'),
  approvePlan: adminPath('/v1/plans/approve'),
  state: adminPath('/v1/controls/state'),
  kill: adminPath('/v1/controls/kill'),
  decideSuggestion: adminPath('/v1/suggestions/decide'),
  openSuggestionIssue: adminPath('/v1/suggestions/open-issue'),
  revoke: adminPath('/v1/agents/revoke'),
  restore: adminPath('/v1/agents/restore'),
  suspend: adminPath('/v1/departments/suspend'),
  resume: adminPath('/v1/departments/resume'),
  grant: adminPath('/v1/assignments/grant'),
  end: adminPath('/v1/assignments/end'),
  autonomy: adminPath('/v1/autonomy/set'),
  admit: adminPath('/v1/tools/admit'),
  withdraw: adminPath('/v1/tools/withdraw'),
  // A vendor's hosted assistant, admitted as a client (ADR-0142).
  hostedClients: adminPath('/v1/hosted-clients/list'),
  admitHostedClient: adminPath('/v1/hosted-clients/admit'),
  withdrawHostedClient: adminPath('/v1/hosted-clients/withdraw'),
  depart: adminPath('/v1/humans/revoke'),
  invite: adminPath('/v1/people/invite'),
  withdrawInvitation: adminPath('/v1/people/withdraw'),
  preset: adminPath('/v1/toolsets/preset'),
  presetAssign: adminPath('/v1/toolsets/assign'),
  executeErasure: adminPath('/v1/erasure/execute'),
  refuseErasure: adminPath('/v1/erasure/refuse'),
  liftSuppression: adminPath('/v1/suppression/lift'),
  refuseLift: adminPath('/v1/suppression/refuse'),
  connections: adminPath('/v1/connections/list'),
  setKey: adminPath('/v1/connections/set'),
  rotateKey: adminPath('/v1/connections/rotate'),
  revokeKey: adminPath('/v1/connections/revoke'),
  rotationTerm: adminPath('/v1/connections/rotation-term'),
  setTerm: adminPath('/v1/terms/set'),
  // What each vendor gives free, and what may be paid beyond it (ADR-0095).
  vendors: adminPath('/v1/vendors/list'),
  // What is switched on and what is left (ADR-0097).
  readiness: adminPath('/v1/readiness'),
  setup: adminPath('/v1/setup'),
  setupMark: adminPath('/v1/setup/mark'),
  setVendorPlan: adminPath('/v1/vendors/plan/set'),
  setVendorAllowance: adminPath('/v1/vendors/allowance/set'),
  pauseVendor: adminPath('/v1/vendors/pause'),
  resumeVendor: adminPath('/v1/vendors/resume'),
  delegations: adminPath('/v1/delegations/list'),
  grantDelegation: adminPath('/v1/delegations/grant'),
  revokeDelegation: adminPath('/v1/delegations/revoke'),
  // A project across departments, who is on it and what it lends
  // (Amendment 0008, ADR-0123); a department created live (ADR-0124).
  openProject: adminPath('/v1/projects/open'),
  addParticipant: adminPath('/v1/projects/members/add'),
  removeParticipant: adminPath('/v1/projects/members/remove'),
  lendTool: adminPath('/v1/projects/tools/grant'),
  revokeLentTool: adminPath('/v1/projects/tools/revoke'),
  createDepartment: adminPath('/v1/departments/create'),
  addAgent: adminPath('/v1/agents/create'),
  // A department's shortlists confirming themselves (Amendment 0012).
  autoconfirmStop: adminPath('/v1/autoconfirm/stop'),
  autoconfirmStart: adminPath('/v1/autoconfirm/start'),
  // One tool for one twin's task, and where the notice that it waits goes
  // (Amendment 0011, ADR-0139).
  taskToolsPending: adminPath('/v1/tasks/tools/pending'),
  grantTaskTool: adminPath('/v1/tasks/tools/grant'),
  refuseTaskTool: adminPath('/v1/tasks/tools/refuse'),
  revokeTaskTool: adminPath('/v1/tasks/tools/revoke'),
  earnedGranting: adminPath('/v1/tasks/tools/earned'),
  addNotifyChannel: adminPath('/v1/notify/channels/add'),
  // The superadmin's write to the platform, refused, or its record stopped and started (ADR-0172).
  operationPerform: adminPath('/v1/operations/perform'),
  operationRefuse: adminPath('/v1/operations/refuse'),
  operationEarned: adminPath('/v1/operations/earned'),
  briefOpenIssue: adminPath('/v1/briefs/open-issue'),
  removeNotifyChannel: adminPath('/v1/notify/channels/remove'),
};

export const SESSION_KEY = ['session'] as const;

export function useMe() {
  return useQuery<Me>({ queryKey: SESSION_KEY, queryFn: fetchMe, staleTime: 5 * 60_000, retry: false });
}

export function useSwitchDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: switchDepartment,
    onSuccess: async () => {
      // Keep the switch pending until /auth/me and scoped readers have
      // refreshed; a destination must not mount against the old session.
      await qc.invalidateQueries();
    },
  });
}

/** The day, the week, or two days either side; anchored on a date. */
export function useToday(span: Span, anchor: Date, department: string) {
  const post = useRecordPost();
  return useQuery<TodayResponse>({
    queryKey: ['today', department, span, anchor.toISOString().slice(0, 10)],
    queryFn: () => post<TodayResponse>(P.today, { span, anchor: anchor.toISOString() }),
  });
}

/** One line of "since you left" (ADR-0088): a count or the platform's own sentence, and where it points. */
export interface WireRecapLine {
  kind: string;
  text: string;
  deepLink: string;
  at: string | null;
}

export interface RecapResponse {
  band: 'since_you_left' | 'what_happened' | 'plan';
  since: string | null;
  until: string | null;
  lines: WireRecapLine[];
}

/**
 * What happened while the person was away, or on a past day; nothing for a
 * day ahead. `null` is today as the person's own zone has it: the server
 * decides which day that is, so a browser in another zone, or the hours just
 * after midnight east of Greenwich, never turn "since you left" into
 * yesterday.
 */
export function useRecap(day: string | null, department: string) {
  const post = useRecordPost();
  return useQuery<RecapResponse>({
    queryKey: ['recap', department, day ?? 'today'],
    queryFn: () => post<RecapResponse>(P.recap, day ? { day } : {}),
  });
}

/**
 * The person opened their day. Recorded at most once per ten minutes by the
 * platform; the recap is asked again afterwards so a new sitting shows what
 * happened since the last one ended.
 */
export function useArrive() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ recorded: boolean; since: string }>(P.arrive, {}),
    onSuccess: (r) => {
      if (r.recorded) void qc.invalidateQueries({ queryKey: ['recap'] });
    },
  });
}

export function useEvents(span: Span, anchor: Date, department: string, enabled = true) {
  const post = useRecordPost();
  return useQuery<EventsResponse>({
    queryKey: ['events', department, span, anchor.toISOString().slice(0, 10)],
    queryFn: () => post<EventsResponse>(P.events, { span, anchor: anchor.toISOString() }),
    enabled,
  });
}

export function useThreads(department: string) {
  const post = useRecordPost();
  return useQuery<{ threads: WireThread[] }>({
    queryKey: ['threads', department],
    queryFn: () => post(P.threads, { limit: 100 }),
  });
}

/**
 * One kind of mail, listed on its own so a busy box cannot push another out
 * of a shared hundred: your own inbox, the department's, or what arrived in
 * a shared box (ADR-0120, ADR-0121).
 */
export function useThreadList(department: string, scope: 'own' | 'department' | 'shared', enabled = true) {
  const post = useRecordPost();
  return useQuery<{ threads: WireThread[] }>({
    queryKey: ['threads', department, scope],
    queryFn: () => post(P.threads, { limit: 100, scope }),
    enabled,
  });
}

export function useThread(threadId: string | null) {
  const post = useRecordPost();
  return useQuery<ThreadReadResponse>({
    queryKey: ['thread', threadId],
    queryFn: () => post(P.threadRead, { threadId }),
    enabled: threadId !== null,
  });
}

/** A person's own task, closed as done or no longer needed. */
export function useCloseTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; state: 'done' | 'cancelled' }) => post<{ closed: string; state: string }>(P.taskClose, input),
    onSuccess: () => {
      for (const key of ['tasks', 'task', 'task-board', 'today', 'twin-tasks']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

const TASK_KEYS = ['tasks', 'task', 'task-board', 'task-notes', 'today', 'twin-task', 'twin-tasks', 'task-reviews', 'company-workspace'] as const;
function refreshTasks(qc: ReturnType<typeof useQueryClient>) {
  for (const key of TASK_KEYS) void qc.invalidateQueries({ queryKey: [key] });
}

/** One task, with whether this person may change it and its time in the calendar (ADR-0149). */
export function useTask(taskId: string) {
  const post = useRecordPost();
  return useQuery<{ task: WireTask }>({
    queryKey: ['task', taskId],
    queryFn: () => post(P.task, { taskId }),
    enabled: taskId.length > 0,
    retry: false,
  });
}

/** Who a task may be given to (ADR-0157): here, its people and agents; elsewhere, its agents and lead. */
export interface WireAssignees {
  department: string;
  people: { id: string; displayName: string; isLead: boolean; role?:'lead'|'member'; openTasks?:number; away?: { endsOn: string; capacityPct: number } }[];
  /** `canWork`: whether the agent holds a model to think with; one that does not could not start a task. */
  agents: { id: string; givenName: string; handle: string; role?:'department_agent'; openTasks?:number; tools?:string[]; canWork?: boolean }[];
}

export function useAssignees(department: string, toDepartment?: string | null) {
  const post = useRecordPost();
  return useQuery<WireAssignees>({
    queryKey: ['task-assignees', department, toDepartment ?? null],
    queryFn: () => post(P.taskAssignees, toDepartment ? { toDepartment } : {}),
    // Asked of another department only once one is chosen.
    enabled: toDepartment !== null,
  });
}

/** Who a task is for, as a body names it. */
export type WireAssignee = { me: true } | { humanId: string } | { agentId: string };

export interface TaskCreateInput {
  title: string;
  body?: string | null;
  priority?: number;
  dueAt?: string | null;
  projectId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  assignee: WireAssignee;
  blockedBy?: string[];
}

export function useCreateTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskCreateInput) => post<{ taskId: string; created: boolean; assignedTo: string }>(P.taskCreate, { ...input }),
    onSuccess: () => refreshTasks(qc),
  });
}

export function useAssignTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; assignee: WireAssignee }) => post<{ assignedTo: string }>(P.taskAssign, { ...input }),
    onSuccess: () => refreshTasks(qc),
  });
}

/** Why something goes from the work, in the platform's fixed words (ADR-0158). */
export type RemovalCategory = 'duplicate' | 'not_needed' | 'not_mine' | 'noise' | 'wrong_source' | 'done_elsewhere' | 'other';

export interface RemovalResult {
  proposed: boolean;
  countedAgainst: string | null;
  senderDomain?: string | null;
  ruleId?: string;
}

export function useRemoveTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; category: RemovalCategory; reason: string }) => post<RemovalResult>(P.taskRemove, { ...input }),
    onSuccess: () => refreshTasks(qc),
  });
}

export function useRemoveThread() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { threadId: string; category: RemovalCategory; reason: string; notWork?: boolean }) => post<RemovalResult>(P.threadRemove, { ...input }),
    onSuccess: () => {
      for (const key of ['today', 'threads', 'thread', 'mail-rules']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export interface WireMailRule {
  id: string;
  scope: 'mine' | 'department';
  department: string | null;
  matchKind: 'domain' | 'address';
  match: string | null;
  action: 'not_work' | 'always_work';
  source: string;
  createdBy: string;
  createdAt: string;
}

export interface WireRemovalSuggestion {
  id: string;
  scope: 'mine' | 'department';
  sourceKind: 'sender_domain' | 'agent' | 'procedure' | 'origin';
  sourceKey: string;
  seen: number;
  proposedAt: string;
}

export function useMailRules(department: string) {
  const post = useRecordPost();
  return useQuery<{ rules: WireMailRule[]; suggestions: WireRemovalSuggestion[]; mayDecideDepartment: boolean }>({
    queryKey: ['mail-rules', department],
    queryFn: () => post(P.mailRules, {}),
  });
}

export function useAddMailRule() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { scope: 'mine' | 'department'; domain?: string; address?: string; action: 'not_work' | 'always_work'; source?: 'removal' }) =>
      post<{ id: string; existing: boolean }>(P.mailRuleAdd, { ...input }),
    onSuccess: () => {
      for (const key of ['mail-rules', 'today', 'threads']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useRevokeMailRule() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => post<{ revoked: true }>(P.mailRuleRevoke, { ruleId }),
    onSuccess: () => {
      for (const key of ['mail-rules', 'today', 'threads']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useDeclineSuggestion() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (suggestionId: string) => post<{ declined: true }>(P.removalDecline, { suggestionId }),
    onSuccess: () => {
      for (const key of ['mail-rules', 'directions']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export interface WireDirection {
  id: string;
  department: string;
  agentId: string | null;
  body: string | null;
  held: boolean;
  source: string;
  createdBy: string;
  createdAt: string;
}

export function useDirections(department: string) {
  const post = useRecordPost();
  return useQuery<{ directions: WireDirection[]; agents: Array<{ id: string; givenName: string; handle: string }>; suggestions: WireRemovalSuggestion[]; mayDirect: boolean }>({
    queryKey: ['directions', department],
    queryFn: () => post(P.directions, {}),
  });
}

export function useAddDirection() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; agentId?: string | null; source?: 'removal' }) => post<{ id: string; held: boolean }>(P.directionAdd, { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['directions'] }),
  });
}

export function useRevokeDirection() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (directionId: string) => post<{ revoked: true }>(P.directionRevoke, { directionId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['directions'] }),
  });
}

/** Who is away or part-time, now or soon (proposal 0012, E2). */
export interface WireTimeAway {
  id: string;
  humanId: string;
  displayName: string;
  kind: 'travel' | 'leave' | 'part_time' | 'busy';
  startsOn: string;
  endsOn: string;
  capacityPct: number;
  covered: boolean;
}

export function useTimeAway() {
  const post = useRecordPost();
  return useQuery<{ away: WireTimeAway[] }>({ queryKey: ['away'], queryFn: () => post(P.awayList, {}) });
}

export function useSetTimeAway() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: WireTimeAway['kind']; startsOn: string; endsOn: string; capacityPct?: number; personId?: string }) => post<{ away: WireTimeAway }>(P.awaySet, { ...input }),
    onSuccess: () => {
      for (const key of ['away', 'task-assignees', 'slots']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useEndTimeAway() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (awayId: string) => post<{ ended: true }>(P.awayEnd, { awayId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['away'] }),
  });
}

export function useSetPlanRests() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { planId: string; rests: Array<{ from: string; to: string; why: string }> }) => post<{ rests: Array<{ from: string; to: string; why: string }> }>(P.planRests, { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['plans'] }),
  });
}

/** The Tasks board: the person's own, the ones they gave someone, or — for the lead — everyone's here. */
export function useTaskBoard(department: string, everyone: boolean) {
  const post = useRecordPost();
  return useQuery<{ tasks: WireTask[]; lead: boolean; assignees?: Omit<WireAssignees, 'department'> }>({
    queryKey: ['task-board', department, everyone],
    queryFn: () => post(P.taskBoard, { everyone }),
  });
}

export interface TaskEditInput {
  taskId: string;
  title?: string;
  body?: string | null;
  priority?: number;
  dueAt?: string | null;
  deferUntil?: string | null;
  estimateMinutes?: number | null;
  boardRank?: number | null;
  state?: 'open' | 'in_progress' | 'waiting';
}

export function useUpdateTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskEditInput) => post<{ changed: string[] }>(P.taskUpdate, { ...input }),
    onSuccess: () => refreshTasks(qc),
  });
}

/** The lead approves or declines what an agent proposed, once (ADR-0151). */
export function useDecideHeld() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; decision: 'approved' | 'declined' }) => post<{ heldId: string; jobId: string | null }>(P.taskApproveHeld, { ...input }),
    onSuccess: () => refreshTasks(qc),
  });
}

/** What is said about a task; polled while an elaboration is on its way. */
export function useTaskNotes(taskId: string, waiting = false) {
  const post = useRecordPost();
  return useQuery<{ notes: WireTaskNote[] }>({
    queryKey: ['task-notes', taskId],
    queryFn: () => post(P.taskNotes, { taskId }),
    enabled: taskId.length > 0,
    refetchInterval: waiting ? 3000 : false,
  });
}

export function useAddTaskNote() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; body: string }) => post<{ noteId: string; held: boolean }>(P.taskNoteAdd, input),
    onSuccess: () => refreshTasks(qc),
  });
}

/**
 * A task a person makes of something they are looking at (ADR-0155): the
 * title, and the page it came from by its address. The press is theirs; a
 * second press of the same one answers the first.
 */
export function useAddMyTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; from?: string }) => post<{ taskId: string; existing: boolean }>(P.taskAdd, input),
    onSuccess: () => refreshTasks(qc),
  });
}

export function useElaborateTask() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (input: { taskId: string }) => post<{ jobId: string; existing: boolean }>(P.taskElaborate, input),
  });
}

/**
 * What a department's agent made of a record, for the person who asked
 * (ADR-0165), polled while one is on its way.
 */
export function useRecordElaborations(context: ChatContext | undefined, enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ elaborations: WireRecordElaboration[]; pending: boolean }>({
    queryKey: ['record-elaborations', context ?? null],
    queryFn: () => post(P.recordElaborations, { context }),
    enabled: enabled && context !== undefined,
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : false),
  });
}

export function useElaborateRecord() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { context: ChatContext }) => post<{ jobId: string; existing: boolean }>(P.recordElaborate, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['record-elaborations'] }),
  });
}

/** One thing a research answer is good for (ADR-0175), as the asking department reads it. */
export interface WireResearchUse {
  id: string;
  kind: 'prospect' | 'competitor' | 'supplier' | 'price' | 'check';
  position: number;
  marketCode: string;
  state: 'proposed' | 'taken' | 'declined' | 'failed';
  outcome: string | null;
  note: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  /** The agent that proposed it, by name. */
  foundBy: string | null;
  createdAt: string;
  detail: {
    name: string;
    finding: number;
    criterion: string;
    quote: string | null;
    url: string | null;
    role: string | null;
    price: { amount: number; currency: string; unit: 'kg' | 'l' | 'piece' | 'pack'; product: string } | null;
    website?: string | null;
    finding_text?: string;
    sources?: string[];
    researchedOn: string;
  };
}

export interface WireResearchUses {
  uses: WireResearchUse[];
  pending: boolean;
  lastError: string | null;
  mayTake: boolean;
  plans: Array<{ id: string; name: string }>;
}

/** An answer's uses, polled while the agent is finding them. */
export function useResearchUses(requestId: string | null, enabled: boolean) {
  const post = useRecordPost();
  return useQuery<WireResearchUses>({
    queryKey: ['research-uses', requestId],
    queryFn: () => post(P.researchUses, { requestId }),
    enabled: enabled && requestId !== null,
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : false),
  });
}

export function useFindResearchUses() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string }) => post<{ jobId: string; existing: boolean }>(P.researchUsesFind, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['research-uses'] }),
  });
}

export function useDecideResearchUses() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; take?: string[]; decline?: string[]; planId?: string }) =>
      post<{ taken: Array<{ id: string; outcome: string }>; declined: number; failed: Array<{ id: string; note: string }>; jobs: string[] }>(P.researchUsesDecide, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['research-uses'] }),
  });
}

export function useProposeTaskTime() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (input: { taskId: string }) =>
      post<{ slots: Array<{ from: string; to: string }>; minutes: number }>(P.taskSchedule, { ...input, mode: 'propose' }),
  });
}

export function useScheduleTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; from: string; to: string }) =>
      post<{ eventId: string; onGoogle: boolean; notOnGoogle?: string }>(P.taskSchedule, { ...input, mode: 'slot' }),
    onSuccess: () => {
      refreshTasks(qc);
      for (const key of ['events', 'slots']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useTasks(department: string) {
  const post = useRecordPost();
  return useQuery<{ tasks: WireTask[] }>({
    queryKey: ['tasks', department],
    queryFn: () => post(P.tasks, { limit: 100 }),
  });
}

export interface WorkspaceItem { sourceDepartment?:string|null; kind: string; id: string; title: string; state: string | null; department: string; at: string; href: string | null; excerpt?: string|null; score?: number; attentionKind?:'reply'|'draft'|'overdue'|'blocked'|'decision'|null; attentionReason?:string|null; nextAction?:string|null; section?:'attention'|'planned'|'latest'; attentionRank?:number }
export function useCompanyWorkspace(companyId: string | null, input: { query?: string; kind?: string; scopeDepartment?: string; status?:string; person?:string; agent?:string; from?:string; to?:string; offset?: number; limit?: number; order?: 'latest'|'type'; preview?: boolean } = {}) {
  const post = useRecordPost();
  const me=useContext(MeContext);
  return useQuery<{ items: WorkspaceItem[]; total: number; offset: number; limit: number; counts: Record<string, number>; readAt: string; departments: string[] }>({
    queryKey: ['company-workspace', companyId, input,me?.humanId,me?.department,[...(me?.departments??[])].sort(),[...(me?.leads??[])].sort(),me?.isSuperadmin],
    queryFn: () => post(P.companyWorkspace, { companyId, ...input }),
    enabled: companyId !== null,
    refetchInterval:30_000,
  });
}

export interface CompanyOperations {
 roles:Array<{id:string;role:string;state:string;valid_from:string;valid_to:string|null;owner_human:string|null;owner_agent:string|null;next_action_task:string|null;blocker_task:string|null}>;
 groups:Array<{id:string;relation:string;related_company_id:string;related_company_name:string;valid_from:string;valid_to:string|null;source_url:string|null}>;
 enrichment:Array<{id:string;field_key:string;proposed_value:unknown;sources:Array<{url:string}>;confidence:number;identity_certain:boolean;conflict:boolean;state:string}>;
 freshness:Array<{id:string;source_kind:string;source_ref:string;state:string;last_success_at:string|null;last_error:string|null}>;
}
export function useCompanyOperations(companyId:string|null){const post=useRecordPost();return useQuery<CompanyOperations>({queryKey:['company-operations',companyId],queryFn:()=>post(P.companyOperations,{companyId}),enabled:companyId!==null});}
function useCompanyOperationMutation<T extends Record<string,unknown>>(path:(typeof P)[keyof typeof P]){const post=useRecordPost();const qc=useQueryClient();return useMutation({mutationFn:(input:T)=>post(path,input),onSuccess:()=>void qc.invalidateQueries({queryKey:['company-operations']})});}
export const useRecordCompanyRole=()=>useCompanyOperationMutation<CompanyRelationshipRoleInput>(P.companyRoleRecord);
export const useLinkCompanyGroup=()=>useCompanyOperationMutation<CompanyGroupLinkInput>(P.companyGroupLink);
export const useProposeCompanyEnrichment=()=>useCompanyOperationMutation<CompanyEnrichmentProposalInput>(P.companyEnrichmentPropose);
export const useDecideCompanyEnrichment=()=>useCompanyOperationMutation<CompanyEnrichmentDecisionInput>(P.companyEnrichmentDecide);
export const useRefreshCompanySource=()=>useCompanyOperationMutation<SourceRefreshInput>(P.companySourceRefresh);
export const useRunCompanyBackfill=()=>useCompanyOperationMutation<CompanyBackfillInput>(P.companyBackfill);
export function useGcpCosts(enabled=true){const post=useRecordPost();return useQuery({queryKey:['gcp-costs'],queryFn:()=>post(P.gcpCosts,{}),enabled});}
export const useRecordGcpCosts=()=>useCompanyOperationMutation<GcpCostSnapshotInput>(P.gcpCostsRecord);

export function useManualCommunications(companyId: string | null) {
  const post = useRecordPost();
  const me = useContext(MeContext);
  return useQuery<{ items: ManualCommunicationRecord[]; departments: string[] }>({
    queryKey: ['manual-communications', companyId, me?.humanId, me?.departments],
    queryFn: () => post(P.manualCommunications, { companyId }),
    enabled: companyId !== null,
  });
}

export function useLogManualCommunication() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ManualCommunicationInput) =>
      post<{ record: ManualCommunicationRecord; created: boolean }>(P.manualCommunicationCreate, input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['manual-communications', input.companyId] });
      void qc.invalidateQueries({ queryKey: ['company-workspace', input.companyId] });
    },
  });
}

export function useCommunicationCapabilities() {
  const post = useRecordPost();
  const me = useContext(MeContext);
  return useQuery<{ channels: CommunicationCapability[] }>({
    queryKey: ['communication-capabilities', me?.department, me?.humanId],
    queryFn: () => post(P.communicationCapabilities, {}),
  });
}

export function useSetCommunicationImportWindow() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CommunicationImportInput) => post(P.communicationImport, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['communication-capabilities'] }),
  });
}

export function useCreateCommunicationTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CommunicationTaskInput) => post<{
      task: { id: string; taskId: string; objective: CommunicationTaskInput['objective']; reviewAt: string | null; created: boolean };
      draft: { outcome: string; jobId: string | null } | null;
    }>(P.communicationTaskCreate, input),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['company-workspace', input.companyId] });
    },
  });
}

export function useRecordCommunicationOutcome() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CommunicationOutcomeInput) => post(P.communicationOutcome, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['company-workspace'] });
    },
  });
}

export function useUploadManualCommunicationAttachment() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; manualCommunicationId: string; filename: string; mediaType: string; dataBase64: string }) =>
      post<{ attachment: ManualCommunicationRecord['attachments'][number] }>(P.manualCommunicationAttachmentUpload, input),
    onSuccess: (_data, input) => void qc.invalidateQueries({ queryKey: ['manual-communications', input.companyId] }),
  });
}

export function useDownloadManualCommunicationAttachment() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (attachmentId: string) => post<{ filename: string; mediaType: string; dataBase64: string }>(
      P.manualCommunicationAttachmentDownload,
      { attachmentId },
    ),
  });
}

export function useMailboxes(department: string) {
  const post = useRecordPost();
  return useQuery<{ mailboxes: WireMailbox[] }>({
    queryKey: ['mailboxes', department],
    queryFn: () => post(P.mailboxes, {}),
  });
}

export function useBrief(forKind: WireBrief['forKind'] | null, forId: string | null) {
  const post = useRecordPost();
  return useQuery<{ brief: WireBrief | null }>({
    queryKey: ['brief', forKind, forId],
    queryFn: () => post(P.briefLive, { forKind, forId }),
    enabled: forKind !== null && forId !== null,
  });
}

/** Free slots; with colleagues, only the times they are free too — read as busy, never as their events (ADR-0093). */
/** Somebody find-a-time could not see, and why, in the platform's words (ADR-0094). */
export interface WireUnseen {
  kind: 'human' | 'contact';
  id: string;
  name: string;
  reason: string;
}

/**
 * Free slots; with people, only the times they are free too — colleagues
 * read as busy and never as their events (ADR-0093), and approved outsiders
 * who shared asked live from Google as you (ADR-0094). Whoever could not be
 * seen comes back in `unseen`, never silently as free.
 */
export function useSlots(anchor: Date, durationMinutes: number, enabled: boolean, withHumanIds: readonly string[] = [], withContactIds: readonly string[] = [], everyWeekFor: number | null = null) {
  const post = useRecordPost();
  const humans = [...withHumanIds].sort();
  const contacts = [...withContactIds].sort();
  return useQuery<{ slots: WireSlot[]; durationMinutes: number; source?: 'live' | 'mirror'; unseen?: WireUnseen[]; weeks?: number }>({
    queryKey: ['slots', anchor.toISOString().slice(0, 10), durationMinutes, humans.join(','), contacts.join(','), everyWeekFor ?? 1],
    queryFn: async () => {
      const r = await post<{ slots?: WireSlot[]; weekly?: Array<{ from: string; to: string }>; durationMinutes: number; source?: 'live' | 'mirror'; unseen?: WireUnseen[]; weeks?: number }>(P.slots, {
        span: 'week',
        anchor: anchor.toISOString(),
        durationMinutes,
        ...(humans.length ? { withHumanIds: humans } : {}),
        ...(contacts.length ? { withContactIds: contacts } : {}),
        // A weekly meeting (0153): only the times free every one of those weeks.
        ...(everyWeekFor ? { everyWeekFor } : {}),
      });
      return { ...r, slots: r.weekly ? r.weekly.map((w) => ({ from: w.from, to: w.to })) : (r.slots ?? []) };
    },
    enabled,
  });
}

export interface WireFollowedCalendar {
  id: string;
  name: string | null;
  accessRole: 'freeBusyReader' | 'reader' | 'writerWithoutPrivateAccess' | 'writer' | 'owner';
  primary: boolean;
  selected: boolean;
  hidden: boolean;
  external: boolean;
  /** `own`: your calendar; `events`: read as its events; `busy_only`: shared as free/busy; `not_shown`: hidden or unselected in Google. */
  read: 'own' | 'events' | 'busy_only' | 'not_shown';
  /** `list`: on your Google calendar list; `shared`: a colleague shared it with you in Google, picked up from their side. */
  source?: 'list' | 'shared';
  sharedByName?: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  /** Google says when it changes (ADR-0096), rather than the platform reading it every half hour. */
  live?: boolean;
  /** Why Google would not open that feed, in the platform's words (ADR-0100). */
  feedRefused?: string | null;
}

export interface WireFollowedEvent {
  id: string;
  subscriptionId: string;
  calendarName: string | null;
  title: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  busyOnly: boolean;
  external: boolean;
}

/** The calendars you follow in Google, and what is on them this span (ADR-0094). */
export function useFollowing(anchor: Date, enabled = true) {
  const post = useRecordPost();
  return useQuery<{ consent: boolean; live?: { own: boolean; list: boolean; listRefused?: string | null }; calendars: WireFollowedCalendar[]; events: WireFollowedEvent[] }>({
    queryKey: ['following', anchor.toISOString().slice(0, 10)],
    queryFn: () => post(P.calendarFollowing, { span: 'week', anchor: anchor.toISOString() }),
    enabled,
  });
}

export function useAvailability() {
  const post = useRecordPost();
  return useQuery<{ rules: WireAvailabilityRule[] }>({
    queryKey: ['availability'],
    queryFn: () => post(P.availabilityGet, {}),
  });
}

export function useSetAvailability() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rules: WireAvailabilityRule[]) =>
      post<{ closed: number; opened: number }>(P.availabilitySet, { rules }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['availability'] });
      void qc.invalidateQueries({ queryKey: ['slots'] });
    },
  });
}

export interface Placement {
  itemKind: TimelineItemKind;
  sourceId: string | null;
  op: 'place' | 'move' | 'hide' | 'pin' | 'postpone' | 'restore';
  plannedFor?: string | undefined;
  plannedAt?: string | undefined;
  durationMinutes?: number | undefined;
  rank?: number | undefined;
}

/**
 * One append on the person's own line. Optimistic is safe here in a way it
 * usually is not: a placement is one write and its undo is one write, and
 * the day re-resolves from the line.
 */
export function usePlace() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (placement: Placement) => post<{ entryId: string }>(P.place, { ...placement }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['today'] }),
  });
}

export function useUndo() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: { itemKind: TimelineItemKind; sourceId: string | null }) =>
      post<{ entryId: string | null }>(P.undo, { ...item }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['today'] }),
  });
}

/** Asks for the work; the brief appears on Today a minute later, by the heartbeat. */
export function useBriefMe() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (subject: { forKind: WireBrief['forKind']; forId: string }) =>
      post<{ outcome: string; jobId?: string }>(P.briefCompose, { ...subject }),
  });
}

export function useDraftReply() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (threadId: string) =>
      post<{ outcome: string; jobId?: string }>(P.draftCompose, { threadId }),
  });
}

// --- the side chat ---------------------------------------------------------

/** A page and identifiers — the contract's own type, so nothing else can be added (§11). */
export type ChatContext = ContractChatContext;

export function useOpenChat(context: ChatContext, agentId: string | null = null) {
  const post = useRecordPost();
  return useMutation({
    mutationFn: () =>
      post<{ session: WireChatSession }>(P.chatOpen, { context, ...(agentId ? { agentId } : {}) }),
  });
}

/** Who the person may talk to: their twin, then the department's agents (ADR-0089). */
export interface WireChatAgent {
  id: string | null;
  handle: string | null;
  givenName: string;
  kind: 'twin' | 'department';
  role: string | null;
  title: string;
  purpose: string | null;
  intro: string;
  starters: string[];
  state: 'active' | 'not_provisioned' | 'stopped';
}

export function useChatAgents(department: string) {
  const post = useRecordPost();
  return useQuery<{ agents: WireChatAgent[] }>({
    queryKey: ['chat-agents', department],
    queryFn: () => post(P.chatAgents, {}),
  });
}

/** A past chat, titled by how it began (FR-DAY-10). */
export interface WireChatListing {
  id: string;
  agentId: string | null;
  agentName: string | null;
  title: string | null;
  startedAt: string;
  lastTurnAt: string | null;
  closedAt: string | null;
}

export function useChatHistory(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ sessions: WireChatListing[] }>({
    queryKey: ['chat-history'],
    queryFn: () => post(P.chatRead, { includeClosed: true }),
    enabled,
  });
}

// --- people talking to each other (ADR-0089) ----------------------------------

export interface WireConversationListing {
  id: string;
  with: { humanId: string; displayName: string; email: string };
  lastMessageAt: string | null;
  createdAt: string;
  /** The other person wrote since you last opened it (ADR-0101). */
  unread?: boolean;
}

export interface WireConversationMessage {
  id: string;
  authorHuman: string;
  writtenByKind: 'human' | 'agent';
  writtenByAgent: string | null;
  state: 'draft' | 'sent';
  fromDraft: string | null;
  body: string | null;
  scanVerdict: 'clean' | 'flagged' | 'blocked';
  createdAt: string;
  /** A draft that has since been sent. */
  sent: boolean;
}

export function useConversations(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ conversations: WireConversationListing[] }>({
    queryKey: ['conversations'],
    queryFn: () => post(P.conversations, {}),
    enabled,
  });
}

/** The conversation with one person, found or made by the platform. */
export function useOpenConversation() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (withHumanId: string) => post<{ conversationId: string }>(P.conversationOpen, { withHumanId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['conversations'] }),
  });
}

export function useConversation(conversationId: string | null) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useQuery<{
    conversationId: string;
    withHumanId: string;
    messages: WireConversationMessage[];
    topics: Array<{ threadId: string; lastMessageAt: string | null }>;
  }>({
    queryKey: ['conversation', conversationId],
    // Opening it is reading it (ADR-0101): the list's "new" goes with it.
    queryFn: async () => {
      const read = await post<{
        conversationId: string;
        withHumanId: string;
        messages: WireConversationMessage[];
        topics: Array<{ threadId: string; lastMessageAt: string | null }>;
      }>(P.conversationRead, { conversationId });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      return read;
    },
    enabled: conversationId !== null,
  });
}

/** Send: the person's own act (§16). A draft is sent by naming it. */
export function useSendMessage(conversationId: string | null) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { text: string; draftId?: string }) =>
      post<{ messageId: string; scanVerdict: string }>(P.conversationSend, { conversationId, ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

/** Kept to send later: a draft is the person's, and sent only when they name it (§16). */
export function useSaveDraft(conversationId: string | null) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => post<{ draftId: string }>(P.conversationDraft, { conversationId, text }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['conversation', conversationId] }),
  });
}

/** Make what was asked a department agent its task: the person's own press (ADR-0089). */
export function useChatTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; title: string }) =>
      post<{ taskId: string; existing?: boolean; agent: { id: string; givenName: string } }>(P.chatTask, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useTranscript(sessionId: string | null) {
  const post=useRecordPost();
  const q=useInfiniteQuery({queryKey:['chat',sessionId],initialPageParam:undefined as number|undefined,
    queryFn:({pageParam})=>post<{turns:WireChatTurn[];beforeSeq:number|null}>(P.chatRead,{sessionId,...(pageParam?{beforeSeq:pageParam}:{})}),
    getNextPageParam:(page)=>page.beforeSeq??undefined,enabled:sessionId!==null});
  const turns=[...new Map((q.data?.pages.flatMap(p=>p.turns)??[]).map(t=>[t.id,t])).values()].sort((a,b)=>a.seq-b.seq);
  return {...q,data:q.data?{turns}:undefined};
}

export function useSay(sessionId: string | null) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => post<TurnResult>(P.chatSay, { sessionId, text }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['chat', sessionId] }),
    // A draft the twin prepared is in a conversation now (ADR-0101).
    onSuccess: (result) => {
      const prepared = (result as { prepared?: Array<{ eventId?: string }> }).prepared ?? [];
      if (prepared.length) {
        void qc.invalidateQueries({ queryKey: ['conversations'] });
        void qc.invalidateQueries({ queryKey: ['conversation'] });
      }
      // A hold the twin placed is on the person's day now (ADR-0119).
      if (prepared.some((p) => p.eventId)) {
        // The calendar reads `events` and free time `slots`; nothing reads `calendar`.
        for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

// --- S2: the mail is worked ------------------------------------------------

export function useDrafts(department: string, outcome?: WireDraft['outcome']) {
  const post = useRecordPost();
  return useQuery<{ drafts: WireDraft[] }>({
    queryKey: ['drafts', department, outcome ?? 'all'],
    queryFn: () => post(P.drafts, { limit: 100, ...(outcome ? { outcome } : {}) }),
  });
}

/**
 * The decision is the approval (ADR-0053). `body` is the person's edit; the
 * platform measures the distance and keeps what left. Never the decider:
 * the gateway stamps the session's person.
 */
/** Written again the way the person says; what they say is kept as how they write (0129). */
export function useRedraft() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; instruction: string; forDepartment?: boolean }) =>
      post<{ redrafting: 'first_touch' | 'reply'; jobId: string | null; guidanceId: string }>(P.redraft, { ...input }),
    onSettled: () => {
      for (const key of ['drafts', 'thread', 'threads', 'today', 'guidance', 'prospect', 'prospects']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export interface WireGuidance {
  id: string;
  body: string;
  draftKind: 'any' | 'first_touch' | 'reply' | 'own_mail' | 'colleague_message' | 'publication';
  department: string;
  share: 'mine' | 'department';
  source: 'redraft' | 'settings';
  standing: 'personal' | 'proposed' | 'adopted' | 'declined';
  byName: string | null;
  createdAt: string;
}

export function useGuidance(department: string) {
  const post = useRecordPost();
  return useQuery<{ mine: WireGuidance[]; department: WireGuidance[]; canDecide: boolean }>({ queryKey: ['guidance', department], queryFn: () => post(P.guidanceList) });
}

export function useAddGuidance() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; draftKind?: WireGuidance['draftKind']; forDepartment?: boolean }) => post<{ guidance: WireGuidance }>(P.guidanceAdd, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['guidance'] }),
  });
}

export function useDecideGuidance() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { guidanceId: string; adopt: boolean }) => post<{ guidanceId: string; adopted: boolean }>(P.guidanceDecide, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['guidance'] }),
  });
}

export function useRevokeGuidance() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (guidanceId: string) => post<{ revoked: string }>(P.guidanceRevoke, { guidanceId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['guidance'] }),
  });
}

export function useDecideDraft() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; outcome: 'sent_unchanged' | 'sent_edited' | 'discarded'; body?: string }) =>
      post<DecideResult>(P.decide, { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['drafts'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['authority'] });
    },
  });
}

/**
 * Who a draft would go as, and whether it may go at all — asked of the
 * draft itself, so a suppressed recipient or a shared box's rule shows
 * before anybody taps send rather than after.
 */
export function useAuthority(draftId: string | null, channel: WireThread['primaryChannel']) {
  const post = useRecordPost();
  return useQuery<WireSendAuthority>({
    queryKey: ['authority', draftId, channel],
    queryFn: () => post(P.authority, { draftId, channel }),
    enabled: draftId !== null,
  });
}

/** Every market, what the ledger decided, and what a send would be told now. */
export function useMarkets(department: string) {
  const post = useRecordPost();
  return useQuery<MarketsResponse>({
    queryKey: ['markets', department],
    queryFn: () => post(P.markets, {}),
    // The answer is "now", and now moves; a minute is the resolution
    // any window or ceiling on it has.
    refetchInterval: 60_000,
  });
}

export function useSuppression() {
  const post = useRecordPost();
  return useQuery<WireSuppressionCounts>({
    queryKey: ['suppression'],
    queryFn: () => post(P.suppression, {}),
  });
}

/** One address, yes or no. A mutation because it is asked, not listed: nothing caches an address. */
export function useSuppressionCheck() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (address: string) => post<{ suppressed: boolean }>(P.suppressionCheck, { address }),
  });
}

export interface WireLiftRequest {
  id: string;
  evidence: string;
  requestedBy: string;
  requestedAt: string;
  state: 'requested' | 'lifted' | 'refused';
  suppressedFor: WireSuppressionCounts['byReason'][number]['reason'] | null;
  suppressedAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  refusalReason: string | null;
}

/** Asks to lift a suppression (ADR-0111): every one to a superadmin, your own to anyone else. Never an address. */
export function useLiftRequests() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; requests: WireLiftRequest[] }>({ queryKey: ['lift-requests'], queryFn: () => post(P.liftRequests) });
}

function invalidateLifts(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['lift-requests', 'suppression', 'audit']) void qc.invalidateQueries({ queryKey: [key] });
}

export function useAskToLift() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { address: string; evidence: string }) => post<{ requestId: string }>(P.liftRequest, input),
    onSettled: () => invalidateLifts(qc),
  });
}

export function useLiftSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; supervisionToken: string }) =>
      admin<{ requestId: string; wasSuppressedFor: string }>(A.liftSuppression, { ...input }),
    onSettled: () => invalidateLifts(qc),
  });
}

export function useRefuseLift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; reason: string }) => admin<{ requestId: string }>(A.refuseLift, { ...input }),
    onSettled: () => invalidateLifts(qc),
  });
}

export interface WireSequence {
  id: string;
  department: string;
  name: string;
  lang: string;
  templateSubject: string;
  templateBody: string;
  paragraphWords: number;
  state: 'draft' | 'approved' | 'retired';
  createdBy: string;
  createdAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  retiredAt: string | null;
  /** How its readers found it (0155): nobody asked, a review open, the newest answer. */
  read?: 'unread' | 'open' | 'reads_well' | 'needs_changes';
}

/** First-touch templates outside a campaign (ADR-0112). */
export function useSequences(department: string) {
  const post = useRecordPost();
  return useQuery<{ sequences: WireSequence[] }>({ queryKey: ['sequences', department], queryFn: () => post(P.sequences) });
}

export function useSequenceAct() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | { act: 'create'; name: string; lang: string; templateSubject: string; templateBody: string; paragraphWords: number }
        | { act: 'approve' | 'retire'; sequenceId: string },
    ) => {
      const { act, ...body } = input;
      const path = act === 'create' ? P.createSequence : act === 'approve' ? P.approveSequence : P.retireSequence;
      return post<{ sequence: WireSequence }>(path, body);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['sequences'] }),
  });
}

/** First to claim has it; losing the race is an answer, not an error. */
export function useClaim() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => post<ClaimResult>(P.claim, { threadId }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

/** A person confirms what triage only suggested (ADR-0118). */
export function useConfirmLink() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => post<{ confirmed: true }>(P.confirmLink, { linkId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['thread'] }),
  });
}

export interface LinkReview {
 linkId:string;sourceTable:'thread';sourceId:string;sourceTitle:string;department:string;
 subject:{kind:'company'|'contact';id:string};subjectName:string;confidence:number;
 evidence:Array<{code:string;effect:'supports'|'contradicts'|'neutral';label:string}>;
 hardConflict:boolean;filedAutomatically:boolean;
 calibration:{state:'unavailable'|'provisional'|'calibrated';reviewedExamples:number;thresholdExamples:number;lowerBound:number|null};
 evaluation:{preliminaryScore:number;verdict:'belongs'|'different'|'uncertain';reasonCodes:string[];model:string;at:string}|null;
 automation:{eligible:false;reason:string};linkedAt:string;
}
export function useLinkReviews(kind:'company'|'contact'){
 const post=useRecordPost();const me=useContext(MeContext);
 return useQuery<{items:LinkReview[];departments:string[]}>({queryKey:['link-reviews',kind,me?.humanId,me?.departments],queryFn:()=>post(P.linkReviews,{kind}),refetchInterval:30_000});
}
export function useLinkCorrectionChoices(kind:'company'|'contact',q:string){
 const post=useRecordPost();
 return useQuery<{items:Array<{id:string;name:string;detail:string|null}>}>({
  queryKey:['link-correction-choices',kind,q],
  queryFn:async()=>{
   if(kind==='company'){
    const data=await post<{companies:WireCompany[]}>(P.directoryList,{kind,limit:100,...(q?{q}:{})});
    return {items:data.companies.map(company=>({id:company.id,name:company.displayName??company.legalName,detail:company.domain}))};
   }
   const data=await post<{contacts:WireContact[]}>(P.directoryList,{kind,limit:100,...(q?{q}:{})});
   return {items:data.contacts.map(contact=>({id:contact.id,name:contact.fullName,detail:contact.email}))};
  },
  placeholderData:keepPreviousData,
 });
}
export function useDecideLink(){
 const post=useRecordPost();const qc=useQueryClient();return useMutation({mutationFn:(input:{linkId:string;decision:'confirmed'|'rejected'|'corrected';reason:string;subject?:{kind:'company'|'contact';id:string}})=>post<{decided:true}>(P.linkDecide,input),
  onSuccess:()=>{for(const key of ['link-reviews','thread','company-workspace'])void qc.invalidateQueries({queryKey:[key]});}});
}
export function useQueueSubjectLinkEvaluation(){
 const post=useRecordPost();return useMutation({mutationFn:(input:{key:string;linkId:string})=>
  post<{requestId:string;jobId:string;created:boolean;state:'queued'|'running'|'done'|'dead'}>(appPath('/v1/subjects/evaluations/queue'),input)});
}
export interface SubjectLinkBackfillResult {dryRun:boolean;candidates:number;queued:number;reused:number;nextCursor:string|null;done:boolean;reason:'ready'|'queued'|'no_candidates'|'no_evaluator'|'candidates_changed'}
export function useSubjectLinkEvaluationBackfill(){
 const post=useRecordPost();const qc=useQueryClient();return useMutation({mutationFn:(input:{kind:'company'|'contact';dryRun:boolean;limit:number;cursor?:string})=>
  post<SubjectLinkBackfillResult>(appPath('/v1/subjects/evaluations/backfill'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['link-reviews']})});
}
export function useSubjectLinkAutomationPolicies(kind:'company'|'contact',enabled=true){
 const post=useRecordPost();const me=useContext(MeContext);return useQuery({queryKey:['subject-link-policies',me?.humanId,me?.department,kind],
  queryFn:()=>post<{policies:SubjectLinkAutomationPolicy[]}>(appPath('/v1/subjects/policies'),{kind}),enabled});
}
export function useEnableSubjectLinkAutomationPolicy(){
 const post=useRecordPost();const qc=useQueryClient();return useMutation({mutationFn:(input:{kind:'company'|'contact';model:string;validTo:string;reason:string})=>
  post<{policy:SubjectLinkAutomationPolicy}>(appPath('/v1/subjects/policies/enable'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['subject-link-policies']})});
}
export function useStopSubjectLinkAutomationPolicy(){
 const post=useRecordPost();const qc=useQueryClient();return useMutation({mutationFn:(input:{policyId:string;reason:string})=>
  post<{policy:SubjectLinkAutomationPolicy}>(appPath('/v1/subjects/policies/stop'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['subject-link-policies']})});
}

export function useUnclaim() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => post<{ released: true }>(P.unclaim, { threadId }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['thread'] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useHeld(department: string, mine = false, offset = 0, senderId: string | null = null) {
  const post = useRecordPost();
  return useQuery<{ held: WireHeld[]; senders: WireHeldSender[]; total?:number; offset?:number; limit?:number; digestTime: 'morning' | 'evening' | null }>({
    queryKey: ['held', department, mine, offset, senderId],
    queryFn: () => post(P.held, { limit: 50, offset, ...(senderId?{senderId}:{}), ...(mine ? { mine: true } : {}) }),
  });
}

export function useEnableStrangerTriagePolicy() {
  const post=useRecordPost();const qc=useQueryClient();
  return useMutation({mutationFn:(input:StrangerTriagePolicyInput)=>post(P.strangerPolicyEnable,input),onSuccess:()=>void qc.invalidateQueries({queryKey:['held']})});
}

export function useStopStrangerTriagePolicy() {
  const post=useRecordPost();const qc=useQueryClient();
  return useMutation({mutationFn:(input:{policyId:string;reason:string})=>post(P.strangerPolicyStop,input),onSuccess:()=>void qc.invalidateQueries({queryKey:['held']})});
}

export function useQueueStrangerResearch() {
  const post=useRecordPost();const qc=useQueryClient();
  return useMutation({mutationFn:(input:StrangerResearchQueueInput)=>post(P.strangerResearchQueue,input),onSuccess:()=>void qc.invalidateQueries({queryKey:['held']})});
}

export function useOverrideStrangerPriority() {
  const post=useRecordPost();const qc=useQueryClient();
  return useMutation({mutationFn:(input:StrangerPriorityOverrideInput)=>post(P.strangerPriority,input),onSuccess:()=>void qc.invalidateQueries({queryKey:['held']})});
}

/** When in their own day the person is told who wrote to them (ADR-0065). Always the session's person. */
export function useSetDigestTime() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (when: 'morning' | 'evening') => post<{ digestTime: 'morning' | 'evening' }>(P.digestTime, { when }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['held'] }),
  });
}

export function usePromote() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { unresolvedId: string; fullName: string; companyId?: string; roleTitle?: string }) =>
      post<{ contactId: string; companyId: string | null; unattended: boolean; promoted: number; existing: boolean }>(P.promote, { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['held'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

/** Know a sender and approve them in one act, under a token bound to the line (ADR-0065, Q14). */
export function useKnowAndApprove() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { unresolvedId: string; fullName: string; companyId?: string; roleTitle?: string; relationship: Relationship; supervisionToken: string }) =>
      post<{ contactId: string; promoted: number; existing: boolean; relationship: Relationship }>(P.knowAndApprove, { ...input }),
    onSettled: () => {
      for (const key of ['held', 'directory', 'contact', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useDismiss() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    // `notWork`: the sender is a machine, not a company (ADR-0159), so the
    // department's rule stops the next one being held at all.
    mutationFn: (input: string | { unresolvedId: string; notWork: true }) =>
      post<{ dismissed: true; messages: number; ruleId?: string }>(P.dismiss, typeof input === 'string' ? { unresolvedId: input } : { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['held'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

// --- S2: the fixed core as records -----------------------------------------

export function useContacts(q: string) {
  const post = useRecordPost();
  return useQuery<{ contacts: WireContact[] }>({
    queryKey: ['directory', 'contact', q],
    queryFn: () => post(P.directoryList, { kind: 'contact', limit: 100, ...(q ? { q } : {}) }),
    // A new search keeps the last list up while it is asked, so the page does not blank.
    placeholderData: keepPreviousData,
  });
}

export function useCompanies(q: string) {
  const post = useRecordPost();
  return useQuery<{ companies: WireCompany[] }>({
    queryKey: ['directory', 'company', q],
    queryFn: () => post(P.directoryList, { kind: 'company', limit: 100, ...(q ? { q } : {}) }),
    // A new search keeps the last list up while it is asked, so the page does not blank.
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ contact: WireContact & { employments: WireEmployment[] } }>({
    queryKey: ['directory', 'contact', 'one', id],
    queryFn: () => post(P.directoryGet, { kind: 'contact', id }),
    enabled: id !== null,
  });
}

/** Hand a contact to a colleague: the holder or a superadmin (ADR-0118). */
export function useAssignContact() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contactId: string; toEmail: string }) => post<{ assigned: true; to: string }>(P.contactAssign, input),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['directory'] }),
  });
}

/** What a department holds about a company, gathered on its page (0128). */
export interface WireCompanyRelated {
  prospects: Array<{ id: string; name: string | null; stage: string; marketCode: string; fitScore: number | null }>;
  buyers: Array<{ id: string; name: string; marketCode: string }>;
  suppliers: Array<{ id: string; name: string; country: string; role: 'current' | 'alternative' }>;
  signals: Array<{ id: string; kind: string; said: string; seenOn: string; source: string | null; prospectId: string | null }>;
}

export function useCompany(id: string | null) {
  const post = useRecordPost();
  return useQuery<{
    company: WireCompany & { employments: WireEmployment[] };
    written?: { prospectId: string; businessName: string | null; details: NonNullable<WireProspect['details']> } | null;
    details?: NonNullable<WireProspect['details']> | null;
    related?: WireCompanyRelated;
  }>({
    queryKey: ['directory', 'company', 'one', id],
    queryFn: () => post(P.directoryGet, { kind: 'company', id }),
    enabled: id !== null,
  });
}

function invalidateDirectory(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['directory'] });
  void qc.invalidateQueries({ queryKey: ['incoming'] });
}

export function useCreateRecord() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ManualRecordInput) => post<{ id: string; kind: 'company' | 'contact'; created: boolean }>(appPath('/v1/directory/create'), { ...input }),
    onSuccess: () => invalidateDirectory(qc),
  });
}

export function useRecordCandidates(kind: 'company' | 'contact', name: string, address: string) {
  const post = useRecordPost();
  return useQuery({
    queryKey: ['directory', 'candidates', kind, name, address],
    queryFn: () => post<{ candidates: RecordCandidate[] }>(appPath('/v1/directory/candidates'), { kind, name, address }),
    enabled: name.trim().length > 0,
  });
}

export function useDuplicates(kind:'company'|'contact',offset:number,includeSeparated:boolean) {
 const post=useRecordPost();const me=useContext(MeContext);
 return useQuery({queryKey:['duplicates',me?.humanId,kind,offset,includeSeparated],
 queryFn:()=>post<DuplicatePage>(appPath('/v1/duplicates/list'),{kind,offset,limit:25,includeSeparated})});
}
export function useConsolidationHistory(kind:'company'|'contact',offset:number) {
 const post=useRecordPost();const me=useContext(MeContext);
 return useQuery({queryKey:['duplicate-history',me?.humanId,kind,offset],queryFn:()=>post<ConsolidationHistoryPage>(appPath('/v1/duplicates/history'),{kind,offset})});
}
export function useDecideDuplicate() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{kind:DuplicatePair['kind'];leftId:string;rightId:string;disposition:DuplicatePair['disposition'];reason:string})=>post<{saved:boolean}>(appPath('/v1/duplicates/decide'),input),
 onSuccess:()=>{void qc.invalidateQueries({queryKey:['duplicates']});}});
}
export function useQueueDuplicateEvaluation() {
 const post=useRecordPost();
 return useMutation({mutationFn:(input:{key:string;kind:'company'|'contact';leftId:string;rightId:string})=>
  post<{requestId:string;jobId:string;created:boolean;state:'queued'|'running'|'done'|'dead'}>(appPath('/v1/duplicates/evaluations/queue'),input)});
}
export interface DuplicateBackfillResult {dryRun:boolean;candidates:number;queued:number;reused:number;nextCursor:{leftId:string;rightId:string}|null;done:boolean;reason:'ready'|'queued'|'no_candidates'|'no_evaluator'|'candidates_changed'}
export function useDuplicateEvaluationBackfill() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{kind:'company'|'contact';dryRun:boolean;limit:number;cursor?:{leftId:string;rightId:string}|undefined})=>post<DuplicateBackfillResult>(appPath('/v1/duplicates/evaluations/backfill'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['duplicates']})});
}
export function useIdentityAutomationPolicies(kind:'company'|'contact',enabled=true) {
 const post=useRecordPost();const me=useContext(MeContext);
 return useQuery({queryKey:['identity-automation-policies',me?.humanId,me?.department,kind],queryFn:()=>post<{policies:IdentityAutomationPolicy[]}>(appPath('/v1/duplicates/policies'),{kind}),enabled});
}
export function useEnableIdentityAutomationPolicy() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{kind:'company'|'contact';model:string;validTo:string;reason:string})=>post<{policy:IdentityAutomationPolicy}>(appPath('/v1/duplicates/policies/enable'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['identity-automation-policies']})});
}
export function useStopIdentityAutomationPolicy() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{policyId:string;reason:string})=>post<{policy:IdentityAutomationPolicy}>(appPath('/v1/duplicates/policies/stop'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['identity-automation-policies']})});
}

export function useUpdateContact() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contactId: string; fullName?: string; email?: string | null; phone?: string | null }) =>
      post<{ contact: WireContact }>(P.contactUpdate, { ...input }),
    onSettled: () => invalidateDirectory(qc),
  });
}

export function useUpdateCompany() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; legalName?: string; displayName?: string | null; domain?: string | null }) =>
      post<{ company: WireCompany }>(P.companyUpdate, { ...input }),
    onSettled: () => invalidateDirectory(qc),
  });
}

export function useMoveContact() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contactId: string; toCompanyId: string; roleTitle?: string; from?: string }) =>
      post<{ closed: WireEmployment | null; opened: WireEmployment }>(P.move, { ...input }),
    onSettled: () => invalidateDirectory(qc),
  });
}

/** Critical tier (§17, ADR-0063): one single-use token, bound to this party, minted by a person. */
export function useApproveCounterparty() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'contact' | 'company'; id: string; relationship: Relationship; supervisionToken: string }) =>
      post<{ approved: true; relationship: Relationship }>(P.approve, { ...input }),
    onSettled: () => invalidateDirectory(qc),
  });
}

/** A prospect's company, filed if it is not in the core yet and approved, under a token bound to the prospect (PR-15). */
export function useApproveProspectCompany() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; relationship: Relationship; supervisionToken: string }) =>
      post<{ approved: true; companyId: string; filed: boolean; relationship: Relationship }>(P.prospectApproveCompany, { ...input }),
    onSettled: () => {
      invalidateDirectory(qc);
      invalidateProspect(qc);
    },
  });
}

export function useIncoming(subject: WireSubject | null) {
  const post = useRecordPost();
  return useQuery<IncomingResponse>({
    queryKey: ['incoming', subject],
    queryFn: () => post(P.incoming, { subject, limit: 100 }),
    enabled: subject !== null,
  });
}

// --- S2: the flexible layer --------------------------------------------------

export function useFlexEntities(department: string) {
  const post = useRecordPost();
  return useQuery<{ entities: WireFlexEntity[] }>({
    queryKey: ['flex', department, 'entities'],
    queryFn: () => post(P.flexEntities, {}),
  });
}

export interface WirePipeline {
  entity: string;
  statuses: Array<{ status: string; count: number; totals: Array<{ currency: string; total: string }> }>;
  stale: Array<{
    id: string; title: string | null; status: string; value: string | null; currency: string | null;
    companyId: string | null; company: string | null; lastMoved: string;
  }>;
  lost: Array<{ companyId: string | null; company: string | null; count: number; totals: Array<{ currency: string; total: string }> }>;
  /** Null where the live Deal does not ask why (deal_pipeline v1). */
  lostWhy: Array<{ reason: string | null; count: number; totals: Array<{ currency: string; total: string }> }> | null;
  staleAfterDays: number;
}

/** The department's pipeline, from its own Deal records (proposal 0008). */
export function usePipeline(department: string) {
  const post = useRecordPost();
  return useQuery<WirePipeline>({
    queryKey: ['pipeline', department],
    queryFn: () => post<WirePipeline>(P.pipeline, {}),
  });
}

export function useFlexList(department: string, entity: string | null) {
  const post = useRecordPost();
  return useQuery<{ entity: WireFlexEntity; rows: WireFlexRow[] }>({
    queryKey: ['flex', department, entity, 'list'],
    queryFn: () => post(P.flexList, { entity, limit: 100 }),
    enabled: entity !== null,
  });
}

export function useFlexRow(department: string, entity: string | null, id: string | null) {
  const post = useRecordPost();
  return useQuery<{ entity: WireFlexEntity; row: WireFlexRow }>({
    queryKey: ['flex', department, entity, id],
    queryFn: () => post(P.flexGet, { entity, id }),
    enabled: entity !== null && id !== null,
  });
}

/**
 * A person writes one of their department's records (ADR-0110). Only field
 * names and plain values are sent: the department is the session's, and
 * whatever the record sets off acts as the department's agent.
 */
export function useFlexWrite(department: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entity: string; id?: string; values: Record<string, string | number | boolean | null> }) =>
      post<{ record: WireFlexRow }>(P.flexWrite, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['flex', department] });
      void qc.invalidateQueries({ queryKey: ['pipeline', department] });
    },
  });
}

// --- S3: requests between departments (§18) ---------------------------------

export function useRequests(department: string, side: 'inbox' | 'outbox') {
  const post = useRecordPost();
  return useQuery<{ requests: WireRequest[] }>({
    queryKey: ['requests', department, side],
    queryFn: () => post(P.requests, { side }),
  });
}

export function useRequest(requestId: string | null) {
  const post = useRecordPost();
  return useQuery<{ request: WireRequest }>({
    queryKey: ['request', requestId],
    queryFn: () => post(P.requestRead, { requestId }),
    enabled: requestId !== null,
  });
}

/**
 * Where a request stands and why (ADR-0169). Only while it is live: a
 * request answered, refused or withdrawn has nothing left to explain.
 * Refreshed each minute, as the heartbeat moves it.
 */
export function useRequestStanding(requestId: string | null, live: boolean) {
  const post = useRecordPost();
  return useQuery<{ standing: WireStanding }>({
    queryKey: ['request-standing', requestId],
    queryFn: () => post(P.requestStanding, { requestId }),
    enabled: requestId !== null && live,
    refetchInterval: 60_000,
  });
}

/** Where a task stands and why (ADR-0169), while an agent has it. */
export function useTaskStanding(taskId: string | null, live: boolean) {
  const post = useRecordPost();
  return useQuery<{ standing: WireStanding }>({
    queryKey: ['task-standing', taskId],
    queryFn: () => post(P.taskStanding, { taskId }),
    enabled: taskId !== null && live,
    refetchInterval: 60_000,
  });
}

/** The platform's own health: a superadmin's (ADR-0140, ADR-0169). */
export function useOperationsCatalogue(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<WireOperationsCatalogue>({
    queryKey: ['operations-catalogue'],
    queryFn: () => post(P.operationsCatalogue, {}),
    enabled,
  });
}

export function useOperations(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ operations: WireOperation[] }>({
    queryKey: ['operations'],
    queryFn: () => post(P.operationsList, {}),
    enabled,
    // A read finishes within seconds, a job within minutes: polled while the page is open.
    refetchInterval: 10_000,
  });
}

export function useOperation(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ operation: WireOperationDetail }>({
    queryKey: ['operation', id],
    queryFn: () => post(P.operationRead, { operationId: id }),
    enabled: id !== null,
    refetchInterval: 10_000,
  });
}

function invalidateOperations(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['operations'] });
  void qc.invalidateQueries({ queryKey: ['operation'] });
  void qc.invalidateQueries({ queryKey: ['operations-catalogue'] });
  void qc.invalidateQueries({ queryKey: ['workspace-queue'] });
  void qc.invalidateQueries({ queryKey: ['task-tools'] });
}

export function useRunOperation() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { op: string; params: Record<string, string | number> }) =>
      post<{ operationId: string; state: 'queued' | 'proposed'; said: string }>(P.operationRun, input),
    onSettled: () => invalidateOperations(qc),
  });
}

export function useConfirmStanding() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { operations: string[]; days: number }) => post<{ taskId: string; endsAt: string; ended: string | null }>(P.operationsStanding, input),
    onSettled: () => invalidateOperations(qc),
  });
}

/** Ending the standing assignment narrows, so it spends no token: every grant on it ends with it. */
export function useStopStanding() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ taskId: string; stopped: true }>(P.operationsStandingStop, {}),
    onSettled: () => invalidateOperations(qc),
  });
}

/** A superadmin's press for a sensitive write; with a token bound to it for a critical one. */
export function usePerformOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { operationId: string; supervisionToken?: string }) =>
      admin<{ operationId: string; state: 'running' | 'done' | 'failed'; summary: string; handle: string | null }>(A.operationPerform, { ...input }),
    onSettled: () => invalidateOperations(qc),
  });
}

/** Refusing narrows, so it spends no token; it also sets the write's record back to none. */
export function useRefuseOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { operationId: string; reason: string }) => admin<{ operationId: string; state: 'refused' }>(A.operationRefuse, { ...input }),
    onSettled: () => invalidateOperations(qc),
  });
}

/** A sensitive write's record: stopped at once, started again under a token. */
export function useOperationEarned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { op: string; action: 'stop' | 'start'; supervisionToken?: string }) => admin<{ op: string; state: 'stopped' | 'started' }>(A.operationEarned, { ...input }),
    onSettled: () => invalidateOperations(qc),
  });
}

export function useNightly(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ enabled: boolean; audits: WireNightlyAudit[] }>({
    queryKey: ['nightly'],
    queryFn: () => post(P.nightly, {}),
    enabled,
  });
}

export function useSetNightly() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => post<{ enabled: boolean; audits: WireNightlyAudit[] }>(P.nightly, { enabled }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['nightly'] }),
  });
}

export function useNightlyAudit(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ audit: WireNightlyAudit }>({
    queryKey: ['nightly', id],
    queryFn: () => post(P.nightlyRead, { auditId: id }),
    enabled: id !== null,
  });
}

export function useBriefs(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ briefs: WireEngineeringBrief[] }>({ queryKey: ['engineering-briefs'], queryFn: () => post(P.briefs, {}), enabled });
}

/** A brief's text, read when it is copied or opened as an issue. */
export function useReadBrief() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (taskId: string) => post<{ brief: { id: string; title: string; body: string; issue: { number: number; url: string } | null } }>(P.briefRead, { taskId }),
  });
}

/** The superadmin's press: the brief opened as an issue on the platform's own repository, once. */
export function useOpenBriefIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; title: string; body: string }) => admin<{ taskId: string; issueUrl: string; already: boolean }>(A.briefOpenIssue, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['engineering-briefs'] }),
  });
}

export function usePlatformHealth(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<WirePlatformHealth>({
    queryKey: ['platform-health'],
    queryFn: () => post(P.platformHealth, { hours: 24 }),
    enabled,
    refetchInterval: 60_000,
  });
}

function invalidateRequests(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['requests'] });
  void qc.invalidateQueries({ queryKey: ['request'] });
  void qc.invalidateQueries({ queryKey: ['today'] });
  void qc.invalidateQueries({ queryKey: ['tasks'] });
}

/**
 * Ask another department. Routed the moment it is opened: an agent, the
 * lead, or a refusal naming what a person would have to grant — and a
 * refusal is a 200 with `routing.outcome === 'refused'`, not an error.
 */
export function useOpenRequest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toDepartment: string; title: string; body?: string; needs?: { tools: string[]; scopes: string[] }; preferHuman?: string; preferAgent?: string }) =>
      post<{ request: WireRequest; routing: WireRouting }>(P.requestOpen, { ...input }),
    onSettled: () => invalidateRequests(qc),
  });
}

export function useCompleteRequest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; result: string }) => post<{ request: WireRequest }>(P.requestComplete, { ...input }),
    onSettled: () => invalidateRequests(qc),
  });
}

export function useRefuseRequest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; reason: string }) => post<{ refused: true }>(P.requestRefuse, { ...input }),
    onSettled: () => invalidateRequests(qc),
  });
}

export function useCancelRequest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => post<{ cancelled: true }>(P.requestCancel, { requestId }),
    onSettled: () => invalidateRequests(qc),
  });
}

export function useAcknowledgeRequest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => post<{ acknowledged: true }>(P.requestAcknowledge, { requestId }),
    onSettled: () => invalidateRequests(qc),
  });
}

/** Receipts by department, action and outcome — for a lead or a superadmin (amendment 0003). */
export function useActivity(sinceDays: number, enabled: boolean) {
  const post = useRecordPost();
  const since = new Date(Date.now() - sinceDays * 86_400_000).toISOString();
  return useQuery<{ since: string; rows: ActivityRow[] }>({
    queryKey: ['activity', sinceDays],
    queryFn: () => post(P.activity, { since }),
    enabled,
  });
}

// --- S4: oversight (ADR-0073) -------------------------------------------------

/**
 * What waits on this person's decision. `here`: the session department's
 * alone, as a department's own Approvals reads it; without it, everything
 * they decide, as the Control room reads it.
 */
export function useApprovals(here = false) {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; items: WireQueueItem[] }>({
    queryKey: ['approvals', here ? 'here' : 'all'],
    queryFn: () => post(P.approvals, here ? { here: true } : {}),
  });
}

/** The agents this person may see; `here`: the session department's own team alone. */
export function useAgents(here = false, enabled = true) {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; agents: WireAgent[] }>({
    queryKey: ['agents', here ? 'here' : 'all'],
    queryFn: () => post(P.agents, here ? { here: true } : {}),
    enabled,
  });
}

/** One agent; `here`: only if it is on the session department's team (its own agents and the reader's twin). */
export function useAgent(agentId: string | null, here = false) {
  const post = useRecordPost();
  return useQuery<{ agent: WireAgent; assignments: WireAssignment[]; spend: { sinceDays: number; usd: number; calls: number } }>({
    queryKey: ['agent', agentId, here],
    queryFn: () => post(P.agent, { agentId, ...(here ? { here: true } : {}) }),
    enabled: agentId !== null,
  });
}

/** Assignments this person may see, of one agent or all; `here`: the session department's own agents' alone. */
export function useAssignments(agentId?: string, here = false) {
  const post = useRecordPost();
  return useQuery<{ assignments: WireAssignment[] }>({
    queryKey: ['assignments', agentId ?? 'all', here ? 'here' : 'all'],
    queryFn: () => post(P.assignments, { ...(agentId ? { agentId } : {}), ...(here ? { here: true } : {}) }),
  });
}

export function useDepartments(options: { staleTime?: number; enabled?: boolean } = {}) {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; departments: WireDepartment[] }>({
    queryKey: ['departments'],
    queryFn: () => post(P.departments),
    ...options,
  });
}

export function useDepartment(name: string | null) {
  const post = useRecordPost();
  return useQuery<{
    department: WireDepartmentDetail;
    triggers?: WireTrigger[];
    /** What its agents keep noticing and have not yet proposed; the lead's and the superadmin's to read. */
    noticing?: Array<{ id: string; title: string; appliesWhen: string; seen: number; lastSeen: string }>;
    limited: boolean;
  }>({
    queryKey: ['department', name],
    queryFn: () => post(P.department, { name }),
    enabled: name !== null,
  });
}

export function useTools() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; tools: WireTool[]; replacement?: { thresholdEur: number; rate: WireDatedRate | null } }>({
    queryKey: ['tools'],
    queryFn: () => post(P.tools),
  });
}

/** Every market the department researched or the ledger declares, one row per country. */
export function useMarketList(department: string) {
  const post = useRecordPost();
  return useQuery<{ markets: WireMarketRow[] }>({
    queryKey: ['market-list', department],
    queryFn: () => post(P.marketList),
  });
}

export function useMarket(department: string, code: string | null) {
  const post = useRecordPost();
  return useQuery<WireMarket>({
    queryKey: ['market', department, code],
    queryFn: () => post(P.marketGet, { code: code! }),
    enabled: code !== null,
  });
}

/** A report composed on the server from the records — never from the page's text (ADR-0135). */
export function useMarketReport(department: string, code: string | null, product: string | null) {
  const post = useRecordPost();
  return useQuery<{ title: string; markdown: string }>({
    queryKey: ['market-report', department, code, product],
    queryFn: () => post(P.marketReport, { ...(code ? { code } : {}), ...(product ? { product } : {}) }),
  });
}

function invalidateMarkets(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['market-list', 'market', 'market-report']) void qc.invalidateQueries({ queryKey: [key] });
}

/** Go or not now, with the words; the lead's or a superadmin's (PR-01). */
export function useDecideMarket() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; stage: 'go' | 'not_now'; decision: string }) => post<{ decided: string }>(P.marketDecide, { ...input }),
    onSettled: () => invalidateMarkets(qc),
  });
}

/** The route a plan may target, confirmed by a person (PR-04). */
export function useConfirmRoute() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; route: string }) => post<{ confirmed: string }>(P.marketConfirmRoute, { ...input }),
    onSettled: () => invalidateMarkets(qc),
  });
}

/**
 * Ask Executive to research the criteria still open (ADR-0132): a §18
 * request, so its refusal — no grant for the grounded model — is a 200
 * with `routing.outcome === 'refused'`, not an error.
 */
export function useResearchMarket() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; product?: string; refresh?: boolean }) =>
      post<{ request: WireRequest; routing: WireRouting; criteria: string[] }>(P.marketResearch, { ...input }),
    onSettled: () => {
      invalidateRequests(qc);
      invalidateMarkets(qc);
    },
  });
}

/**
 * Ask Nia to find distributors in one market (ADR-0176). Like the market's
 * research, a refusal for want of a grant is a 200 that says so.
 */
export function useResearchDistributors() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; channels?: string[]; maxCandidates?: number }) =>
      post<{ request: WireRequest; routing: WireRouting; country: string }>(P.distributorResearch, { ...input }),
    onSettled: () => invalidateRequests(qc),
  });
}

/** File a report to the shared drive; the ledger mirror writes it on its next run. */
export function useFileReport() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'market_report' | 'portfolio'; code?: string; product?: string }) =>
      post<{ documentId: string; title: string; waitingOn: string }>(P.documentCreate, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

/**
 * The territory map's public key (ADR-0137), asked for only when a map is
 * open: each hand-out is a counted load, so it is never refetched on focus
 * or on a timer, and a refusal — none set, not restricted, the month at its
 * stop line — is the reason the grid is drawn alone.
 */
export function useMapsKey(enabled: boolean) {
  // A global, referrer-restricted browser bootstrap (Amendment 0010), not
  // record content. Keep the original human-only core route and its checks.
  return useQuery<{ key: string; loads: number; stop: number }>({
    queryKey: ['maps-key'],
    queryFn: () => post(P.mapsKey),
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useProfiles(department: string) {
  const post = useRecordPost();
  return useQuery<{ profiles: WireProfile[] }>({ queryKey: ['profiles', department], queryFn: () => post(P.profiles) });
}

export function useTerritories(department: string) {
  const post = useRecordPost();
  return useQuery<{ territories: WireTerritory[] }>({ queryKey: ['territories', department], queryFn: () => post(P.territories) });
}

export function useTerritory(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ territory: WireTerritory }>({
    queryKey: ['territory', id],
    queryFn: () => post(P.territory, { territoryId: id! }),
    enabled: id !== null,
  });
}

/** A department's prospecting plans — not the vendors' plans on Plans and usage. */
export function useProspectingPlans(department: string) {
  const post = useRecordPost();
  return useQuery<{ plans: WirePlan[]; objections: WireObjectionCount[] }>({ queryKey: ['prospecting-plans', department], queryFn: () => post(P.prospectingPlans) });
}

/** A market's own sources, with their standing and last read (ADR-0141). */
export function useMarketSources(department: string, code: string | null) {
  const post = useRecordPost();
  return useQuery<{ sources: WireMarketSource[] }>({
    queryKey: ['market-sources', department, code],
    queryFn: () => post(P.marketSources, { code: code! }),
    enabled: code !== null,
  });
}

export function useSignals(department: string) {
  const post = useRecordPost();
  return useQuery<{ signals: WireSignal[] }>({ queryKey: ['signals', department], queryFn: () => post(P.signals) });
}

function invalidateProspecting(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['profiles', 'territories', 'territory', 'prospecting-plans', 'signals']) void qc.invalidateQueries({ queryKey: [key] });
}

/** A draft profile goes live, the lead's or a superadmin's; a live one changes only by a proposal. */
export function useMakeProfileLive() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => post<{ live: true }>(P.profileLive, { profileId }),
    onSettled: () => invalidateProspecting(qc),
  });
}

/** One day of the plan with nobody contacted and nothing written outside our index (R7); a job on the heartbeat. */
export function useDryRun() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => post<{ jobId: string }>(P.planDryRun, { planId }),
    onSettled: () => invalidateProspecting(qc),
  });
}

/**
 * The superadmin approves a plan at the admin door (Q10, ADR-0134): an
 * `iam.grant_scope` token bound to `plan:<id>` grants the agent an
 * assignment carrying the plan's tools, until the plan's end.
 */
export function useApprovePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { planId: string; agentId: string; supervisionToken: string }) =>
      admin<{ planId: string; assignmentId: string; validTo: string; tools: string[]; handle: string }>(A.approvePlan, { ...input }),
    onSettled: () => {
      invalidateProspecting(qc);
      invalidateOversight(qc);
    },
  });
}

function invalidateProspect(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['prospects', 'prospect', 'requests', 'today', 'erasures', 'intros', 'samples']) void qc.invalidateQueries({ queryKey: [key] });
}

/** Lost needs a reason, and a person gives it (R10); the cadence stops. */
export function useMarkLost() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; reason: 'price' | 'already_supplied' | 'not_now' | 'wrong_person' | 'other'; note?: string }) => post<{ lost: string }>(P.prospectLost, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** A person writes a company's details down (0126): what they read on its site, a register or Google Maps. */
/** A company's details, written down on the company itself for every department (0128). */
export function useWriteCompanyRecord() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyId: string; postalAddress?: string; phone?: string; website?: string; orgNumber?: string; note?: string }) =>
      post<{ details: NonNullable<WireProspect['details']> }>(P.companyDetails, { ...input }),
    onSettled: () => invalidateDirectory(qc),
  });
}

export function useWriteCompanyDetails() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; businessName?: string; postalAddress?: string; phone?: string; website?: string; orgNumber?: string; note?: string }) =>
      post<{ prospect: WireProspect }>(P.prospectDetails, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** File what their reply objected to, or correct the list's reading: appended, never rewritten (R9). */
export function useFileObjection() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; objection: 'price' | 'already_supplied' | 'not_now' | 'wrong_person' | 'other' }) => post<{ objection: string }>(P.prospectObjection, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** Ask a colleague who knows them for an introduction; the cold cadence waits on the answer. */
export function useAskIntro() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; via: string; message?: string }) => post<{ introId: string }>(P.prospectIntro, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** Only the colleague who was asked answers it. */
export function useAnswerIntro() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { introId: string; made: boolean; outcome?: string }) => post<{ introId: string; state: string }>(P.introAnswer, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** A §18 request to Production, and a call task on the asker's day a week later. */
export function useSendSample() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; product: string; format?: string; quantity?: string; note?: string }) =>
      post<{ request?: WireRequest; routing?: WireRouting; existing?: boolean }>(P.prospectSample, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** They said stop: suppressed for every plan, and the cadence ends. */
export function useOptOut() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: string | { prospectId: string; reason?: 'objected' | 'manual' }) =>
      post<{ suppressed: boolean; cadenceStopped: boolean }>(P.prospectOptOut, typeof input === 'string' ? { prospectId: input } : { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

/** They asked to be forgotten: recorded now, executed by a superadmin under a token (ADR-0081). */
export function useEraseProspect() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { prospectId: string; receivedOn: string }) => post<{ request: { id: string } }>(P.prospectErase, { ...input }),
    onSettled: () => invalidateProspect(qc),
  });
}

export interface SetupInput {
  profile: {
    marketCode: string;
    segment: string;
    titles: string[];
    products: string[];
    placesTypes: string[];
    keywords: string[];
    minRating: number | null;
    minReviews: number | null;
    priceLevels: number[];
    rules: Array<{ code: string; label: string; points: number; kind: ProfileRuleKind; param: string | number | number[] | null }>;
    fitThreshold: number;
    channel: 'email' | 'warm_intro' | 'call' | 'visit';
    /** The companies a lookalike was made from (0130). */
    seededFrom?: string[];
  };
  /** Further kinds of company the one plan looks for, each a draft profile of its own (0130). */
  moreProfiles?: Array<SetupInput['profile']>;
  /** The kind's existing profile in this country, planned with instead of `profile`. */
  profileId?: string;
  territory: { marketCode: string; name: string; kind: 'area' | 'guide' | 'fair' | 'country'; city: string | null; center: { lat: number; lng: number } | null; radiusKm: number | null; guide?: string | null; fairOn?: string | null; regions?: string[] };
  plan: {
    perDay: number; until: string; budgetEur: number; gaps: '3/7' | '2/5' | '5/14'; tools: string[]; priceTest?: [number, number];
    looksFor?: 'customers' | 'suppliers' | 'competitors';
    resweepDays?: number;
    /** Named people only above the fit bar (default), or never for this plan (0136). */
    peopleSearch?: 'above_bar' | 'never';
    /** Further kinds the country already has a profile for. */
    moreProfileIds?: string[];
  };
}

/**
 * The set-up wizard (S:21): a draft profile, a territory and a waiting plan
 * in one transaction, landing whole or not at all. Nothing searches until
 * the superadmin approves the plan.
 */
/** A new country (S:21): a market the file declares, opened at `screened` so Nia can be asked to research it. Nothing is decided. */
export function useScreenMarket() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string }) => post<{ marketCode: string; stage: 'screened' }>(P.marketScreen, { ...input }),
    // The wizard's country list reads `market-list`.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['markets'] });
      invalidateMarkets(qc);
    },
  });
}

/** A profile proposed from the clients with a paid order in a market (PR-08); it writes nothing. */
export function useSuggestProfile() {
  const post = useRecordPost();
  return useMutation({
    mutationFn: (input: { marketCode?: string; prospectIds?: string[]; companyIds?: string[]; likeProspectIds?: string[] }) => post<WireProfileSuggestion>(P.profileSuggest, { ...input }),
  });
}

export function useSetupProspecting() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetupInput) =>
      post<{ profileId: string; territoryId: string; planId: string; waitingOn: string }>(P.prospectingSetup, { ...input }),
    onSettled: () => invalidateProspecting(qc),
  });
}

export function useProfileChanges(department: string, recordId?: string) {
  const post = useRecordPost();
  return useQuery<{ changes: WireProfileChange[] }>({ queryKey: ['profile-changes', department, recordId ?? null], queryFn: () => post(P.profileChanges, recordId ? {recordId} : {}) });
}

/** Approving writes the profile's next version; refusing keeps it, with the reason (PR-19). */
export function useDecideProfileChange() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { changeId: string; approve: boolean; reason?: string }) => post<Record<string, unknown>>(P.profileChangeDecide, { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['profile-changes'] });
      invalidateProspecting(qc);
    },
  });
}

export function useIntros(department: string, recordId?: string) {
  const post = useRecordPost();
  return useQuery<{ intros: WireIntro[] }>({ queryKey: ['intros', department, recordId ?? null], queryFn: () => post(P.intros, recordId ? {recordId} : {}) });
}

export function useSamples(department: string) {
  const post = useRecordPost();
  return useQuery<{ samples: WireSample[] }>({ queryKey: ['samples', department], queryFn: () => post(P.samples) });
}

/** What came of a sample: sent, then ordered or not; a person records it. */
export function useSampleOutcome() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sampleId: string; outcome: 'sent' | 'ordered' | 'no_order' }) => post<{ sampleId: string; state: string }>(P.sampleOutcome, { ...input }),
    onSettled: () => {
      for (const key of ['samples', 'prospect', 'profile-changes']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useOurIndex(department: string, recordId?: string) {
  const post = useRecordPost();
  return useQuery<{
    index: { places: number; people: number; lookupsSaved: number; shredded: number };
    savings?: { placeHits: number; personHits: number; eur: number | null; missing: string[]; rate: WireDatedRate | null };
    personHorizonDays: number;
    freshDays: number;
    entries: WireIndexEntry[];
  }>({ queryKey: ['our-index', department, recordId ?? null], queryFn: () => post(P.ourIndex, recordId ? {recordId} : {}) });
}

function invalidateValidation(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['buyers', 'suppliers', 'supplier', 'offers', 'offer', 'today']) void qc.invalidateQueries({ queryKey: [key] });
}

export function useBuyers(department: string) {
  const post = useRecordPost();
  return useQuery<{ buyers: WireBuyer[] }>({ queryKey: ['buyers', department], queryFn: () => post(P.buyers) });
}

/** A buyer is written to only once the lead approves them. */
/** A finding a person records by hand on a market (PR-04): the criterion, what was found and where. */
export function useRecordEvidence() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; criterion: string; finding: string; provenance: 'sourced' | 'judgement'; source?: string; sourceUrl?: string; sourceDate?: string; confidence?: 'high' | 'medium' | 'low'; pestle?: string[]; product?: string }) =>
      post<{ evidenceId: string }>(P.marketEvidence, input),
    onSuccess: () => {
      for (const key of ['market', 'markets', 'market-list', 'market-report']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export interface BuyerDraft {
  marketCode: string;
  companyName: string;
  roleTitle: string;
  source: string;
  confidence: 'high' | 'medium' | 'low';
  companyType?: string;
  website?: string;
  name?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  switchboard?: string;
  suppliedBy?: string;
  prospectId?: string;
}

/** A buyer a person found (B12): a role at a company, and how to reach them, unapproved until the lead says so. */
export function useCreateBuyer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BuyerDraft) => post<{ buyerId: string }>(P.buyerCreate, { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['buyers'] }),
  });
}

export interface SupplierDraft {
  name: string;
  country: string;
  role: 'current' | 'alternative';
  contact?: string;
  asOf?: string;
  priceListValidUntil?: string;
  incoterms?: string;
  orderMinimum?: string;
  companyId?: string;
  documentId?: string;
}

/** A supplier the lead adds (B12): who makes what we sell, and what their price list rests on. */
export function useCreateSupplier() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierDraft) => post<{ supplierId: string }>(P.supplierCreate, { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export interface OfferDraft {
  marketCode: string;
  supplierId: string;
  title: string;
  currency: string;
  validUntil?: string;
  incoterms?: string;
  audience?: string[];
  languages?: string[];
  lines: Array<{ label: string; value?: string; dependsOn?: string }>;
}

/** An offer the lead drafts (Q18): lines that each rest on a supplier answer, prepared only once every one is in writing. */
export function useCreateOffer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OfferDraft) => post<{ offerId: string }>(P.offerCreate, { ...input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['offers'] }),
  });
}

export function useApproveBuyer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buyerId: string) => post<{ approved: true }>(P.buyerApprove, { buyerId }),
    onSettled: () => invalidateValidation(qc),
  });
}

export function useSuppliers(department: string) {
  const post = useRecordPost();
  return useQuery<{ suppliers: WireSupplierRow[] }>({ queryKey: ['suppliers', department], queryFn: () => post(P.suppliers) });
}

export function useSupplier(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ supplier: WireSupplier }>({ queryKey: ['supplier', id], queryFn: () => post(P.supplier, { supplierId: id! }), enabled: id !== null });
}

/** An answer as it was given, with where; appended, never edited. */
export function useRecordAnswer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { supplierId: string; field: string; answer: string; source: string; note?: string; confirm?: { documentId?: string; threadId?: string } }) =>
      post<{ answerId: string; confirmed: boolean }>(P.supplierAnswer, { ...input }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** Confirmed in writing: a document of theirs, or a conversation on file in the department (ADR-0135 §2). */
export function useConfirmAnswer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { answerId: string; documentId?: string; threadId?: string }) => post<{ answerId: string; confirmed: true }>(P.supplierConfirm, { ...input }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** Every question not answered or not in writing, as one message on the asker's day to send themselves. */
export function useAskSupplier() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: string) => post<{ taskId: string; subject: string; body: string; asked: string[] }>(P.supplierAsk, { supplierId }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** The questions went, from the person's own address: the supplier is asked (S:19). */
export function useSupplierAsked() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { supplierId: string; taskId?: string }) => post<{ supplierId: string; askedAt: string }>(P.supplierAsked, { ...input }),
    onSettled: () => {
      invalidateValidation(qc);
      for (const key of ['today', 'tasks', 'task', 'task-board']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useOffers(department: string) {
  const post = useRecordPost();
  return useQuery<{ offers: WireOfferRow[] }>({ queryKey: ['offers', department], queryFn: () => post(P.offers) });
}

export function useOffer(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ offer: WireOffer }>({ queryKey: ['offer', id], queryFn: () => post(P.offer, { offerId: id! }), enabled: id !== null });
}

/** Refused, naming every line on an unconfirmed answer, until each is confirmed (Q18); then its text is the preparer's task. */
export function usePrepareOffer() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerId: string) => post<{ offer: WireOffer; taskId: string }>(P.offerPrepare, { offerId }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** The person who sent it from their own address says so. */
export function useMarkOfferSent() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (offerId: string) => post<{ sent: true }>(P.offerSent, { offerId }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** The supplier's contact, as a person gave it; sealed under the supplier's key (S:19). */
export function useSupplierContact() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { supplierId: string; contact: string }) => post<{ supplierId: string }>(P.supplierContact, { ...input }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** Ask the department's prospecting agent to find who to write to at the supplier; a task for them, or a §18 request where there is none. */
export function useFindSupplierContact() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: string) => post<{ routedTo: 'agent' | 'request'; agent?: string; taskId?: string; routing?: WireRouting }>(P.supplierFindContact, { supplierId }),
    onSettled: () => invalidateValidation(qc),
  });
}

/** Address a prepared offer to a buyer: its text on the person's day, to them (S:20). */
export function useSendOfferTo() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { offerId: string; buyerId: string }) => post<{ recipientId: string; taskId: string }>(P.offerSendTo, { ...input }),
    onSettled: () => invalidateValidation(qc),
  });
}

export function useMarkRecipientSent() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: string) => post<{ sent: true }>(P.offerRecipientSent, { recipientId }),
    onSettled: () => invalidateValidation(qc),
  });
}

export interface WireProspectingReport {
  window: { days: number; from: string; to: string };
  plans: Array<Pick<WirePlan, 'id' | 'marketCode' | 'segment' | 'territory' | 'state' | 'budgetEur' | 'spentEur' | 'funnel' | 'costPerQualifiedLead'>>;
  markets: Array<{ marketCode: string; started: number; replied: number; outOfOffice: number; bounced: number; lost: number; silence: number; open: number; replyRate: number | null }>;
  samples: { asked: number; sent: number; ordered: number; noOrder: number; orderRate: number | null };
  tools: Array<{ toolId: string; calls: number; refused: number; ceilingHits: number; byReason: Record<string, number>; origin: string | null; vendor: string | null; vendorCostMonthUsd: number | null }>;
  objections: WireObjectionCount[];
  priceTests?: Array<{ planId: string; arm: 'A' | 'B'; eur: number; started: number; replied: number; ordered: number }>;
  index: { places: number; people: number; lookupsSaved: number; shredded: number };
}

/** The prospecting report (S:24), counted from the records and the audit chain; its lead's and a superadmin's. */
export function useProspectingReport(department: string, days: number, enabled: boolean) {
  const post = useRecordPost();
  return useQuery<WireProspectingReport>({
    queryKey: ['prospecting-report', department, days],
    queryFn: () => post(P.prospectingReport, { days }),
    enabled,
  });
}

export interface WireBatchItem {
  prospectId: string;
  businessName: string | null;
  marketCode: string;
  planId: string;
  score: number;
  threshold: number;
  topReasons: WireProfileRule[];
  whyNow: string | null;
  opening: boolean;
  signals: string[];
  /** Its own domain, and the core company that domain is, if filed (PR-15). */
  domain: string | null;
  company: { id: string; name: string; relationship: string } | null;
  /** Who was found to write to: an approved contact by name, or the kind of address kept, or nobody yet. */
  found?: { kind: 'named'; name: string; title: string | null } | { kind: 'shared_address' | 'personal_address' | 'nobody_yet' };
}

/** The morning batch (S:01, PR-14): what cleared a running plan's bar in the last day, openings first, and who placed it. */
export function useMorningBatch(department: string) {
  const post = useRecordPost();
  return useQuery<{ top: WireBatchItem[]; more: number; plans: number; placedBy: { givenName: string; email: string } | null }>({
    queryKey: ['morning-batch', department],
    queryFn: () => post(P.morningBatch),
    staleTime: 5 * 60_000,
  });
}

export function useFiledDocuments(department: string) {
  const post = useRecordPost();
  return useQuery<{ documents: WireFiledDocument[] }>({
    queryKey: ['documents', department],
    queryFn: () => post(P.documentList),
  });
}

/** What the session's department's agents may use; its lead's and a superadmin's to read. */
/** Where one tool is attached in every department, for the superadmin's Tool library (F4). */
export function useToolWhere(toolId: string | null) {
  const post = useRecordPost();
  return useQuery<{ toolId: string; where: Array<{ department: string; level: 'department' | 'plan'; plan: { id: string; name: string } | null; agents: string[]; until: string | null }> }>({
    queryKey: ['tool-where', toolId],
    queryFn: () => post(P.toolWhere, { toolId }),
    enabled: toolId !== null,
  });
}

export function useToolAttachments(department: string, enabled = true) {
  const post = useRecordPost();
  return useQuery<{ department: string; tools: WireAttachedTool[]; countedFrom: string }>({
    queryKey: ['tool-attachments', department],
    queryFn: () => post(P.toolAttachments),
    enabled,
  });
}

export function useAudit(sinceDays: number, actor: string | null, action: string | null = null) {
  const post = useRecordPost();
  const since = new Date(Date.now() - sinceDays * 86_400_000).toISOString();
  return useQuery<{ since: string; viewer: WireViewer; rows: WireAuditRow[] }>({
    queryKey: ['audit', sinceDays, actor, action],
    // `who`: `actor` is a key the gateway owns and the client refuses to send.
    queryFn: () => post(P.audit, { since, limit: 200, ...(actor ? { who: actor } : {}), ...(action ? { action } : {}) }),
    // Typing a filter keeps the rows on screen until the narrower page arrives.
    placeholderData: keepPreviousData,
  });
}

/** Where the twin's trail is read: a department, a project or a twin's task (0122). */
export type TrailContext = { inDepartment: string } | { projectId: string } | { taskId: string };

/**
 * The viewer's own twin, where the viewer is looking. The body names the
 * room and never the twin: the database finds it from the person.
 */
export function useTwinTrail(context: TrailContext, enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ since: string; rows: WireAuditRow[]; readsOfMe?: WireAuditRow[] }>({
    queryKey: ['twin-trail', context],
    queryFn: () => post(P.twinTrail, context),
    enabled,
  });
}

export function useChain() {
  const post = useRecordPost();
  return useQuery<WireChain>({
    queryKey: ['audit', 'chain'],
    queryFn: () => post(P.auditVerify),
  });
}

export function useCost(days: number) {
  const post = useRecordPost();
  return useQuery<WireCostSummary>({
    queryKey: ['cost', days],
    queryFn: () => post(P.cost, { days }),
  });
}

/** The gateway's own state, read through the admin door. Superadmin only. */
export function useControls(enabled: boolean) {
  return useQuery<WireControls>({
    queryKey: ['controls'],
    queryFn: () => admin(A.state),
    enabled,
    refetchInterval: 15_000,
  });
}

function invalidateOversight(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['approvals', 'agents', 'agent', 'assignments', 'departments', 'department', 'tools', 'audit', 'controls', 'session', 'workspace-queue']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * Mint a supervision token for one operation and one target (§17). The
 * plaintext comes back once and is spent by the flow that asked for it.
 */
export function useMintToken() {
  return useMutation({
    mutationFn: (input: { operation: string; target: string }) => admin<WireMinted>(A.mint, { ...input }),
  });
}

/** A suggestion's spec, accepted (and opened as an issue) or declined with a reason (ADR-0150). */
export function useDecideSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; decision: 'accepted' | 'declined'; reason?: string; spec?: string; startChange?: boolean }) =>
      admin<{ decision: string; issueUrl: string | null; issueNote: string | null; changeRequestId: string | null }>(A.decideSuggestion, { ...input }),
    onSuccess: () => {
      for (const key of ['request', 'requests', 'workspace-queue']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/** An accepted suggestion's issue, opened after the decision, with the spec the superadmin read (ADR-0156). */
export function useOpenSuggestionIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; spec: string }) => admin<{ issueUrl: string }>(A.openSuggestionIssue, { ...input }),
    onSuccess: () => {
      for (const key of ['request', 'requests', 'workspace-queue']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { engaged: boolean; supervisionToken: string }) =>
      admin<{ killSwitchEngaged: boolean }>(A.kill, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useRevokeAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; reason: string; supervisionToken: string }) =>
      admin<{ handle: string; revoked: true }>(A.revoke, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useRestoreAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; supervisionToken: string }) =>
      admin<{ handle: string; revoked: false }>(A.restore, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useSuspendDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { department: string; reason: string; supervisionToken: string }) =>
      admin<{ department: string; suspended: true }>(A.suspend, { name: input.department, reason: input.reason, supervisionToken: input.supervisionToken }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useResumeDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { department: string; supervisionToken: string }) =>
      admin<{ department: string; suspended: false }>(A.resume, { name: input.department, supervisionToken: input.supervisionToken }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useGrantAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; title: string; processRef?: string; validTo: string; tools: string[]; scopes: string[]; supervisionToken: string }) =>
      admin<{ assignmentId: string }>(A.grant, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useEndAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) => admin<{ assignmentId: string; validTo: string }>(A.end, { assignmentId }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useSetAutonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { department: string; level: AutonomyLevel; dimension: AutonomyDimension; evidence?: string; supervisionToken?: string }) =>
      // The department it moves is named `name`: a body key called
      // `department` is only ever the session's (ADR-0073, ADR-0118).
      admin<{ from: AutonomyLevel; to: AutonomyLevel }>(A.autonomy, (({ department, ...rest }) => ({ name: department, ...rest }))(input)),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useAdmitTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toolId: string; note: string; supervisionToken: string }) => admin<{ admitted: boolean }>(A.admit, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

export function useWithdrawTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toolId: string; note: string; supervisionToken: string }) => admin<{ admitted: boolean }>(A.withdraw, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

/** Every hosted assistant ever admitted, newest first, with what hangs off each (ADR-0142). */
export function useHostedClients(enabled: boolean) {
  return useQuery<{ clients: WireHostedClient[] }>({
    queryKey: ['hosted-clients'],
    queryFn: () => admin(A.hostedClients),
    enabled,
  });
}

export function useAdmitHostedClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vendor: string; redirectUris: string[]; note: string; supervisionToken: string }) =>
      admin<{ admissionId: string; vendor: string; redirectUris: string[] }>(A.admitHostedClient, { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['hosted-clients'] });
      invalidateOversight(qc);
    },
  });
}

export function useWithdrawHostedClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vendor: string; note: string }) =>
      admin<{ vendor: string; withdrawn: boolean; registrations: number; grantsEnded: number; tokensEnded: number }>(A.withdrawHostedClient, { ...input }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['hosted-clients'] });
      invalidateOversight(qc);
    },
  });
}

// --- S5: make it mine, put it back (§9, ADR-0028, ADR-0062, §13) -----------

export function useExperience() {
  const post = useRecordPost();
  return useQuery<WireExperienceLine>({
    queryKey: ['experience'],
    queryFn: () => post(P.experienceLine),
  });
}

function invalidateMine(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['experience', 'promotions', 'rules', 'preferences', 'toolsets', 'tokens', 'approvals']) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/** Replace the personal layer. The line keeps every earlier version; nothing is lost by this. */
export function usePersonalise() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WireExperiencePayload) =>
      post<{ version: WireExperienceVersion; resolved: WireExperiencePayload }>(P.personalise, { payload }),
    onSettled: () => invalidateMine(qc),
  });
}

/** Put it back: to one earlier version of my own line, or to the shared baseline with none. */
export function useResetExperience() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string | null) =>
      post<{ version: WireExperienceVersion; resolved: WireExperiencePayload }>(P.experienceReset, versionId ? { versionId } : {}),
    onSettled: () => invalidateMine(qc),
  });
}

export function usePromotions(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ promotions: WirePromotion[] }>({
    queryKey: ['promotions'],
    queryFn: () => post(P.promotions),
    enabled,
  });
}

export function useProposePromotion() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { versionId: string; rationale: string }) => post<{ proposal: WirePromotion }>(P.propose, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useDecidePromotion() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { proposalId: string; decision: 'promote' | 'reject' }) =>
      post<{ decision: string }>(P.decidePromotion, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useRules() {
  const post = useRecordPost();
  return useQuery<{ rules: WireRule[] }>({ queryKey: ['rules'], queryFn: () => post(P.rules) });
}

export function useAddRule() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: RuleKind;
      contactId?: string;
      companyId?: string;
      copyContactId?: string;
      windowStart?: string;
      windowEnd?: string;
      windowDays?: number[];
      windowZone?: string;
    }) => post<{ rule: WireRule }>(P.ruleAdd, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useRevokeRule() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => post<{ revoked: true }>(P.ruleRevoke, { ruleId }),
    onSettled: () => invalidateMine(qc),
  });
}

export function usePreferences() {
  const post = useRecordPost();
  return useQuery<{ preferences: WirePreference[] }>({ queryKey: ['preferences'], queryFn: () => post(P.preferences) });
}

export function useAddPreference() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => post<{ preference: WirePreference }>(P.preferenceAdd, { body }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useRevokePreference() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (preferenceId: string) => post<{ revoked: true }>(P.preferenceRevoke, { preferenceId }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useToolsets() {
  const post = useRecordPost();
  return useQuery<{ toolsets: WireToolset[] }>({ queryKey: ['toolsets', 'all'], queryFn: () => post(P.toolsets) });
}

export function useMyToolsets() {
  const post = useRecordPost();
  return useQuery<{ toolsets: WireToolset[] }>({ queryKey: ['toolsets', 'mine'], queryFn: () => post(P.toolsetsMine) });
}

export function useSelfSelect() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => post<{ toolsets: WireToolset[] }>(P.selfSelect, { name }),
    onSettled: () => invalidateMine(qc),
  });
}

/** A preset: what a job function starts with, decided by a superadmin (§13). */
export function useDefinePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; jobFunction: string; description?: string; tools: string[] }) =>
      admin<{ toolset: WireToolset }>(A.preset, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

/** Give a preset to a person, by their address (§13). */
export function useAssignPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; email: string }) =>
      admin<{ assigned: true; name: string; email: string }>(A.presetAssign, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useDefineToolset() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; jobFunction: string; description?: string; tools: string[] }) =>
      post<{ toolset: WireToolset }>(P.defineToolset, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useTokens() {
  const post = useRecordPost();
  return useQuery<{ tokens: WireToken[] }>({ queryKey: ['tokens'], queryFn: () => post(P.tokensList) });
}

export function useMintPersonalToken() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; days: number; mcpOnly?: boolean }) => post<{ token: string; id: string; expiresAt: string; mcpOnly: boolean }>(P.tokensMint, { ...input }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useRevokePersonalToken() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => post<{ revoked: boolean }>(P.tokensRevoke, { tokenId }),
    onSettled: () => invalidateMine(qc),
  });
}

export function useConstitution() {
  const post = useRecordPost();
  return useQuery<{ path: string; text: string }>({ queryKey: ['ledger', 'constitution'], queryFn: () => post(P.constitution), staleTime: 3_600_000 });
}

export function useDecisions() {
  const post = useRecordPost();
  return useQuery<{ decisions: WireDecisionRow[] }>({ queryKey: ['ledger', 'decisions'], queryFn: () => post(P.decisions), staleTime: 3_600_000 });
}

export function useDecision(number: string | null) {
  const post = useRecordPost();
  return useQuery<{ number: string; file: string; text: string }>({
    queryKey: ['ledger', 'decision', number],
    queryFn: () => post(P.decision, { number }),
    enabled: number !== null,
    staleTime: 3_600_000,
  });
}

/**
 * The change loop (S6, ADR-0074). Between the two human steps the heartbeat
 * does the work, so a change that is moving is re-read every few seconds —
 * and one that waits on a person, or is finished, is not.
 */
const MOVING: ReadonlySet<string> = new Set([
  'read_back_confirmed',
  'building',
  'sandbox_deployed',
  'testing',
  'tests_passed',
  'approved',
  'promoting',
]);

export function isMoving(change: WireChange | WireChangeDetail): boolean {
  if (change.state === 'asked') return true;
  return change.build !== null && MOVING.has(change.build.state);
}

function invalidateChanges(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['changes', 'change', 'approvals', 'workspace-queue']) void qc.invalidateQueries({ queryKey: [key] });
}

export function useChanges(department: string) {
  const post = useRecordPost();
  return useQuery<{ changes: WireChange[] }>({
    queryKey: ['changes', department],
    queryFn: () => post(P.changes),
    refetchInterval: (q) => (q.state.data?.changes.some(isMoving) ? 3_000 : false),
  });
}

export function useChange(changeRequestId: string) {
  const post = useRecordPost();
  return useQuery<{ change: WireChangeDetail; mayConfirm: boolean; mayDecide: boolean; mayLook?: boolean }>({
    queryKey: ['change', changeRequestId],
    queryFn: () => post(P.change, { changeRequestId }),
    refetchInterval: (q) => {
      const c = q.state.data?.change;
      if (!c) return false;
      // A model that cannot be reached leaves the compile job dead; there is
      // nothing to wait for then, and the screen says so instead of spinning.
      if (c.state === 'asked' && c.compile?.state === 'dead') return false;
      return isMoving(c) ? 3_000 : false;
    },
  });
}

/** The person who asked says whether the change, working in its sandbox, is what they meant (ADR-0150). */
export function useLookAtPreview() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { changeRequestId: string; meant: boolean; reason?: string }) =>
      post<{ meant: boolean; handedToEngineer: boolean }>(P.changePreview, { ...input }),
    onSettled: () => invalidateChanges(qc),
  });
}

export function useStartChange() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { intent: string; specPath?: string }) =>
      post<{ changeRequestId: string; buildId: string | null; source: 'model' | 'engineer' }>(P.changeStart, { ...input }),
    onSettled: () => invalidateChanges(qc),
  });
}

export function useConfirmChange() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changeRequestId: string) => post<{ buildId: string }>(P.changeConfirm, { changeRequestId }),
    onSettled: () => invalidateChanges(qc),
  });
}

export function useAbandonChange() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changeRequestId: string) => post<{ changeRequestId: string }>(P.changeAbandon, { changeRequestId }),
    onSettled: () => invalidateChanges(qc),
  });
}

export function useApproveBuild() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buildId: string) => post<{ buildId: string; state: string }>(P.buildApprove, { buildId }),
    onSettled: () => invalidateChanges(qc),
  });
}

export function useReturnBuild() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { buildId: string; reason: string }) =>
      post<{ buildId: string; state: string; handedToEngineer: boolean }>(P.buildReturn, { ...input }),
    onSettled: () => invalidateChanges(qc),
  });
}

/** Proposed merges on this person's day, or touching one thread (ADR-0031). */
export function useMergeProposals(department: string, threadId: string | null = null) {
  const post = useRecordPost();
  return useQuery<{ proposals: WireMergeProposal[] }>({
    queryKey: ['merges', department, threadId],
    queryFn: () => post(P.merges, threadId ? { threadId } : {}),
  });
}

export function useDecideMerge() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { proposalId: string; decision: 'merge' | 'keep_apart' }) =>
      post<{ state: 'merged' | 'declined'; keptThreadId: string }>(P.mergeDecide, { ...input }),
    onSettled: () => {
      for (const key of ['merges', 'today', 'threads', 'thread']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/** Projects and a voice earned inside one (ADR-0059). */
function invalidateProjects(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['projects', 'project']) void qc.invalidateQueries({ queryKey: [key] });
}

export function useProjects(department: string) {
  const post = useRecordPost();
  return useQuery<{ projects: WireProject[] }>({ queryKey: ['projects', department], queryFn: () => post(P.projects) });
}

export function useProject(projectId: string) {
  const post = useRecordPost();
  return useQuery<{ project: WireProjectDetail; member: boolean; myTwin: { handle: string; granted: boolean } | null }>({
    queryKey: ['project', projectId],
    queryFn: () => post(P.project, { projectId }),
  });
}

/**
 * A project's room (Amendment 0009, ADR-0125): what its participants said,
 * and its meetings. Asked again every few seconds while a meeting is under
 * way, so each agent's turn appears as it is taken.
 */
export function useProjectRoom(projectId: string) {
  const post = useRecordPost();
  return useQuery<WireRoom>({
    queryKey: ['project-room', projectId],
    queryFn: () => post(P.projectRoom, { projectId }),
    refetchInterval: (q) => (q.state.data?.meetings.some((m) => m.state === 'running' || m.state === 'minuting') ? 4_000 : false),
  });
}

export function useSayInRoom() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; body: string }) => post<{ messageId: string; scanVerdict: string }>(P.projectSay, { ...input }),
    onSettled: (_r, _e, input) => void qc.invalidateQueries({ queryKey: ['project-room', input.projectId] }),
  });
}

export function useConveneMeeting() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; agenda: string; rounds: number }) =>
      post<{ meetingId: string; speakers: number; firstDepartment: string }>(P.projectConvene, { ...input }),
    onSettled: (_r, _e, input) => void qc.invalidateQueries({ queryKey: ['project-room', input.projectId] }),
  });
}

export function useCreateProject() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; title: string; description?: string }) => post<{ projectId: string }>(P.projectCreate, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

export function useAddProjectMember() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; who: string }) => post<{ added: string }>(P.projectAddMember, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

export function useAskForDraft() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; channel: PublicationChannel }) => post<{ jobId: string; twin: string }>(P.projectDraft, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

export function useDecidePublication() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { publicationId: string; outcome: 'posted_unchanged' | 'posted_edited' | 'discarded'; final?: string }) =>
      post<{ outcome: string; editDistance: number | null }>(P.publicationDecide, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

/** A project's level, at the admin door (ADR-0059): raising spends a token bound to the project. */
export function useSetProjectLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; level: AutonomyLevel; evidence?: string; supervisionToken?: string }) =>
      admin<{ from: AutonomyLevel; to: AutonomyLevel }>(A.autonomy, { ...input }),
    onSettled: () => {
      invalidateProjects(qc);
      invalidateOversight(qc);
    },
  });
}

/** A twin given a project, at the admin door: the token is bound to the twin and the project. */
export function useGrantProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agentId: string; projectId: string; title: string; validTo: string; tools: string[]; supervisionToken: string }) =>
      admin<{ assignmentId: string }>(A.grant, { ...input, scopes: [] }),
    onSettled: () => {
      invalidateProjects(qc);
      invalidateOversight(qc);
    },
  });
}

/** A project one department leads, or none, opened by a superadmin (ADR-0123 §5). Opening spends no token; joining does. */
export function useOpenProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; title: string; description?: string; leadDepartment: string | null; owner?: string }) =>
      admin<{ projectId: string; leadDepartment: string | null }>(A.openProject, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

/** A person or a department agent, from any department, put on a project under a token bound to the project and them. */
export function useAddParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; who: string; role: 'member' | 'observer'; supervisionToken: string }) =>
      admin<{ projectId: string; who: string; role: string; kind: 'human' | 'agent' }>(A.addParticipant, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

/** Taking someone off a project narrows what reaches them, so it spends no token. */
export function useRemoveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; who: string }) => admin<{ projectId: string; who: string }>(A.removeParticipant, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

/** One tool lent to one project (Amendment 0008), under a token bound to the pair; refused past any participant's department floor. */
export function useLendTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { projectId: string; toolId: string; validTo?: string; supervisionToken: string }) =>
      admin<{ id: string; projectId: string; toolId: string }>(A.lendTool, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

export function useRevokeLentTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { grantId: string }) => admin<{ grantId: string }>(A.revokeLentTool, { ...input }),
    onSettled: () => invalidateProjects(qc),
  });
}

/** A department created live, under a token bound to its name (ADR-0124). The answer says whether the ledger was written. */
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      displayName: string;
      purpose: string;
      sponsor: string;
      grantableScopes: string[];
      neverScopes: string[];
      supervisionToken: string;
    }) => admin<WireDepartmentCreated>(A.createDepartment, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

/** An agent added to a department live (ADR-0151): its ledger line committed under a token bound to the department and the name. */
export function useAddAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { inDepartment: string; givenName: string; role: string; purpose: string; sponsor: string; scopes: string[]; tools: string[]; supervisionToken: string }) =>
      admin<{ commitSha: string; email: string; next: string }>(A.addAgent, { ...input }),
    onSettled: () => invalidateOversight(qc),
  });
}

/** Who a hold is with: colleagues and approved counterparties, by id (ADR-0093). */
export interface HoldAttendees {
  humanIds: string[];
  contactIds: string[];
}

export interface HoldResult {
  eventId: string;
  heldBy: string;
  attendees: string[];
  onGoogle: boolean;
  /** Why the hold stayed on the platform, in words, when it did. */
  notOnGoogle?: string;
}

/** A weekly meeting placed as one series of holds (0153): the weeks placed, and those no longer free. */
export interface SeriesResult {
  seriesId: string;
  placed: Array<HoldResult & { index: number; from: string; to: string }>;
  skipped: Array<{ index: number; from: string; why: string }>;
}

/** A hold on your own calendar, placed by your twin (ADR-0067); anyone on it is told nothing yet. */
export function useHold() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { from: string; to: string; title: string; attendees?: HoldAttendees; calendar?: string; repeatWeeks?: number }) =>
      post<HoldResult & Partial<SeriesResult>>(P.calendarHold, {
        from: input.from,
        to: input.to,
        title: input.title,
        ...(input.repeatWeeks ? { repeatWeeks: input.repeatWeeks } : {}),
        // A calendar shared with them, by its id on their list (0138); the server checks it again.
        ...(input.calendar ? { calendar: input.calendar } : {}),
        ...(input.attendees?.humanIds.length ? { attendeeHumanIds: input.attendees.humanIds } : {}),
        ...(input.attendees?.contactIds.length ? { attendeeContactIds: input.attendees.contactIds } : {}),
      }),
    onSettled: () => {
      for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/** Send a hold's invitations: your own press, as you, through your Google calendar (ADR-0093). */
export function useInvite() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => post<{ eventId: string; invited: number }>(P.calendarInvite, { eventId }),
    onSettled: () => {
      for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/** Move a meeting whose invitations went out: your own press, and Google tells every guest, from you (ADR-0121). */
export function useMoveMeeting() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { eventId: string; from: string; to: string }) =>
      post<{ eventId: string; from: string; to: string }>(P.calendarMove, { eventId: input.eventId, from: input.from, to: input.to }),
    onSettled: () => {
      for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/** Cancel a meeting whose invitations went out: Google tells every guest, from you. Cancelled, never deleted. */
export function useCancelMeeting() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => post<{ eventId: string; cancelled: true }>(P.calendarCancel, { eventId }),
    onSettled: () => {
      for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useReleaseHold() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => post<{ released: string }>(P.calendarRelease, { eventId }),
    onSettled: () => {
      for (const key of ['events', 'slots', 'today']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

// --- S7: what happened (0049) -------------------------------------------------

/**
 * A person's notifications. Polled, because nothing pushes: a minute is
 * soon enough for "a build is waiting on you", and a tab left open all day
 * costs one small read a minute.
 */
export function useNotifications(unreadOnly = false) {
  const post = useRecordPost();
  return useQuery<{ items: WireNotification[]; unread: number }>({
    queryKey: ['notifications', unreadOnly],
    queryFn: () => post(P.notifications, { unreadOnly, limit: 100 }),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (which: { ids: string[] } | { all: true }) => post<{ marked: number }>(P.notificationsRead, which),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useIncidents() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; incidents: WireIncident[] }>({
    queryKey: ['incidents'],
    queryFn: () => post(P.incidents),
  });
}

export function useIncident(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ incident: WireIncident }>({
    queryKey: ['incident', id],
    queryFn: () => post(P.incident, { id }),
    enabled: id !== null,
  });
}

export function useAcknowledgeIncident() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; note?: string }) => post<{ incident: WireIncident }>(P.acknowledgeIncident, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['incidents'] });
      void qc.invalidateQueries({ queryKey: ['incident'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['workspace-queue'] });
    },
  });
}

export function useLedgerProposals() {
  const post = useRecordPost();
  return useQuery<{ proposals: WireLedgerProposal[] }>({
    queryKey: ['ledger-proposals'],
    queryFn: () => post(P.ledgerProposals),
  });
}

export function useLedgerProposal(id: string | null) {
  const post = useRecordPost();
  return useQuery<{ proposal: WireLedgerProposalDetail; ledgerText: string | null }>({
    queryKey: ['ledger-proposal', id],
    queryFn: () => post(P.ledgerProposal, { id }),
    enabled: id !== null,
  });
}

/** Decline every open edit shown to be the mirror's own write, in one press (0183). */
export function useDeclineMirrorsOwn() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ declined: number }>(P.declineMirrorsOwn, {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['ledger-proposals'] });
      void qc.invalidateQueries({ queryKey: ['ledger-proposal'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['workspace-queue'] });
    },
  });
}

export function useDecideLedgerProposal() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; decision: 'accepted' | 'declined'; reason?: string }) =>
      post<{ proposal: WireLedgerProposalDetail }>(P.decideLedgerProposal, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['ledger-proposals'] });
      void qc.invalidateQueries({ queryKey: ['ledger-proposal'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['workspace-queue'] });
    },
  });
}

export function usePeople() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; people: WirePerson[] }>({ queryKey: ['people'], queryFn: () => post(P.people) });
}

export function usePerson(personId: string | null) {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; person: WirePersonDetail }>({
    queryKey: ['person', personId],
    queryFn: () => post(P.person, { personId }),
    enabled: personId !== null,
  });
}

export function useDriveFolders(companyId?: string) {
  const post = useRecordPost();
  return useQuery<{ drive: string; relationship?: string; folders: WireFolder[] }>({
    queryKey: ['drive-folders', companyId ?? null],
    queryFn: () => post(P.driveFolders, companyId ? { companyId } : {}),
    staleTime: 10 * 60_000,
  });
}

export function useInvitations() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; invitations: WireInvitation[] }>({ queryKey: ['invitations'], queryFn: () => post(P.invitations) });
}

/** Someone should join: checked against the roster's rules, with the lines a person commits (0059). */
export function useInvitePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; displayName: string; function: string; sponsors: string[]; twin: { givenName: string; purpose: string } }) =>
      admin<{ invitation: { id: string; rosterEntry: string } }>(A.invite, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

export function useWithdrawInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { invitationId: string }) => admin<{ invitationId: string }>(A.withdrawInvitation, { ...input }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
}

/** A person leaves (ADR-0057 as amended): access ends, their work goes to a named successor. */
export function useRecordDeparture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; transferTo: string; folderKey?: string; supervisionToken: string }) =>
      admin<{ personId: string; transferTo: string; folderKey: string | null; moved: WireMoved; shredAfter: string }>(A.depart, { ...input }),
    onSettled: () => {
      invalidateOversight(qc);
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
    },
  });
}

export function useErasures() {
  const post = useRecordPost();
  return useQuery<{ viewer: WireViewer; requests: WireErasure[] }>({ queryKey: ['erasures'], queryFn: () => post(P.erasures) });
}

export function useErasure(requestId: string | null) {
  const post = useRecordPost();
  return useQuery<{ request: WireErasure }>({
    queryKey: ['erasure', requestId],
    queryFn: () => post(P.erasure, { requestId }),
    enabled: requestId !== null,
  });
}

function invalidateErasures(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['erasures', 'erasure', 'contact', 'directory', 'audit', 'workspace-queue']) void qc.invalidateQueries({ queryKey: [key] });
}

/** Record that a subject asked to be forgotten, on the day they asked. */
export function useRequestErasure() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contactId: string; receivedOn: string }) => post<{ request: WireErasure }>(P.requestErasure, input),
    onSettled: () => invalidateErasures(qc),
  });
}

export function useExecuteErasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; supervisionToken: string }) =>
      admin<{ requestId: string; outcome: NonNullable<WireErasure['outcome']> }>(A.executeErasure, { ...input }),
    onSettled: () => invalidateErasures(qc),
  });
}

export function useRefuseErasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requestId: string; reason: string }) => admin<{ requestId: string }>(A.refuseErasure, { ...input }),
    onSettled: () => invalidateErasures(qc),
  });
}

// --- Prospects (ADR-0064) ------------------------------------------------------

export function useProspects(department: string, stage: ProspectStage | null = null) {
  const post = useRecordPost();
  return useQuery<{ prospects: WireProspect[] }>({
    queryKey: ['prospects', department, stage],
    queryFn: () => post(P.prospects, stage ? { stage } : {}),
    // Each step is a job on the heartbeat; a minute is how fast the list moves.
    refetchInterval: 60_000,
  });
}

export function useProspect(prospectId: string | null) {
  const post = useRecordPost();
  return useQuery<{ prospect: WireProspect; coldContact: string; record: WireProspectRecord | null; steps?: WireResearchStep[] }>({
    queryKey: ['prospect', prospectId],
    queryFn: () => post(P.prospect, { prospectId }),
    enabled: prospectId !== null,
  });
}

function invalidateProspects(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['prospects'] });
  void qc.invalidateQueries({ queryKey: ['prospect'] });
}

export function useDiscover() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { query: string; marketCode: string; limit?: number }) => post<{ jobId: string; agent: string }>(P.discover, { ...input }),
    onSettled: () => invalidateProspects(qc),
  });
}

export function useExcludeProspect() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prospectId: string) => post<{ prospect: WireProspect }>(P.excludeProspect, { prospectId }),
    onSettled: () => invalidateProspects(qc),
  });
}

export function useDraftProspect() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prospectId: string) => post<{ jobId: string }>(P.draftProspect, { prospectId }),
    onSettled: () => invalidateProspects(qc),
  });
}

/** A prospect stuck before its site is read, or read with no address found, tried again. */
export function useRetryProspect() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prospectId: string) => post<{ jobId: string; step: 'read_site' | 'enrich_contact' }>(P.retryProspect, { prospectId }),
    onSettled: () => invalidateProspects(qc),
  });
}

export function useImportProspects() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: { domain: string; marketCode: string; businessName?: string }[]) =>
      post<{ created: number; problems: string[] }>(P.importProspects, { rows }),
    onSettled: () => invalidateProspects(qc),
  });
}

// --- Lifecycle (ADR-0082, ADR-0087) -------------------------------------------------

export function useLifecycleContacts(department: string) {
  const post = useRecordPost();
  return useQuery<WireLifecycleContacts>({
    queryKey: ['lifecycle', department],
    queryFn: () => post(P.lifecycleContacts, {}),
    // The heartbeat syncs; a minute is enough to see it land.
    refetchInterval: 60_000,
  });
}

export function useLifecycleContact(lifecycleId: string | null) {
  const post = useRecordPost();
  return useQuery<{ contact: WireLifecycleContact }>({
    queryKey: ['lifecycle-contact', lifecycleId],
    queryFn: () => post(P.lifecycleContact, { lifecycleId }),
    enabled: lifecycleId !== null,
  });
}

function invalidateLifecycle(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['lifecycle'] });
  void qc.invalidateQueries({ queryKey: ['lifecycle-contact'] });
}

export interface BasisRecord {
  /** A new contact is named by the address, which is sealed and never shown again. */
  address?: string;
  lifecycleId?: string;
  basis: LifecycleBasis;
  evidenceKind: LifecycleEvidence;
  evidenceRef?: string;
  source: string;
  marketCode?: string;
  leadType?: WireLifecycleContact['leadType'];
}

export function useRecordBasis() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (record: BasisRecord) => post<{ lifecycleId: string; basisId: string }>(P.lifecycleRecord, { ...record }),
    onSettled: () => invalidateLifecycle(qc),
  });
}

export function useWithdrawBasis() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (basisId: string) => post<{ withdrawn: boolean }>(P.lifecycleWithdraw, { basisId }),
    onSettled: () => invalidateLifecycle(qc),
  });
}

export interface LifecycleImportRow {
  address: string;
  basis: string;
  source: string;
  date: string;
  marketCode?: string;
}

export function useImportLifecycle() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: LifecycleImportRow[]) => post<{ recorded: number; problems: string[] }>(P.lifecycleImport, { rows }),
    onSettled: () => invalidateLifecycle(qc),
  });
}

export function useSyncLifecycle() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lifecycleId: string) => post<{ queued: boolean }>(P.lifecycleSync, { lifecycleId }),
    onSettled: () => invalidateLifecycle(qc),
  });
}

/** Tell Customer.io one thing happened; judged now, sent by the heartbeat (ADR-0102). */
export function useTrackLifecycle() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { lifecycleId: string; event: string }) => post<{ queued: boolean; jobId: string }>(P.lifecycleTrack, input),
    onSettled: () => invalidateLifecycle(qc),
  });
}

export function useOutreachReport(department: string) {
  const post = useRecordPost();
  return useQuery<{ digests: WireOutreachDigest[] }>({
    queryKey: ['outreach-report', department],
    queryFn: () => post(P.outreachReport, {}),
  });
}

// --- Connections (ADR-0083, ADR-0085) --------------------------------------------

export function useConnections(enabled: boolean) {
  return useQuery<WireConnections>({ queryKey: ['connections'], queryFn: () => admin(A.connections), enabled });
}

function invalidateConnections(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['connections'] });
  void qc.invalidateQueries({ queryKey: ['audit'] });
}

/**
 * Set or rotate a key. The value goes in the body once. A mutation keeps
 * its variables in the cache until it is collected, so this one is
 * collected at once: the key does not outlive the dialog that sent it.
 */
export function useSetKey() {
  const qc = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: { ref: string; value: string; supervisionToken: string; rotate: boolean }) =>
      admin<{ ref: string; kind: 'set' | 'rotate'; version: string; setAt: string }>(input.rotate ? A.rotateKey : A.setKey, {
        ref: input.ref,
        value: input.value,
        supervisionToken: input.supervisionToken,
      }),
    onSettled: () => invalidateConnections(qc),
  });
}

export function useRevokeKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => admin<{ ref: string; revoked: boolean }>(A.revokeKey, { ref }),
    onSettled: () => invalidateConnections(qc),
  });
}

export function useRotationTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ref: string; days: number; supervisionToken?: string }) => admin<{ from: number; to: number }>(A.rotationTerm, { ...input }),
    onSettled: () => invalidateConnections(qc),
  });
}

export function useSetTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { level: TermLevel; ref?: string; days: number; supervisionToken?: string }) =>
      admin<{ from: number; to: number }>(A.setTerm, { ...input }),
    onSettled: () => invalidateConnections(qc),
  });
}

// --- Vendor plans and allowances (ADR-0095) --------------------------------------

export function useReadiness(enabled: boolean) {
  return useQuery<WireReadiness>({
    queryKey: ['readiness'],
    queryFn: () => admin(A.readiness),
    enabled,
    // Read from the platform's own state; a minute is fresh enough to watch a step land.
    refetchInterval: 60_000,
  });
}

// --- The Workspace (ADR-0117) ------------------------------------------------------

/** The setup roadmap: every step a person takes, decided by Readiness or a superadmin's mark. */
export function useSetup(enabled: boolean) {
  return useQuery<WireSetup>({
    queryKey: ['setup'],
    queryFn: () => admin(A.setup),
    enabled,
    refetchInterval: 60_000,
  });
}

/** A superadmin's word on a step no check can see. */
export function useMarkSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { stepId: string; done: boolean; note?: string }) => admin(A.setupMark, { ...input }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['setup'] }),
  });
}

/** Everything that waits on a superadmin, the approvals queue's kinds and the rest. */
export function useWorkspaceQueue(enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ items: WireWorkspaceItem[] }>({
    queryKey: ['workspace-queue'],
    queryFn: () => post(P.workspaceQueue),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useVendors(enabled: boolean) {
  return useQuery<WireVendors>({
    queryKey: ['vendors'],
    queryFn: () => admin(A.vendors),
    enabled,
    // The gateway counts as calls are made; a minute is soon enough to see it.
    refetchInterval: 60_000,
  });
}

function invalidateVendors(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['vendors'] });
  void qc.invalidateQueries({ queryKey: ['audit'] });
}

export function useSetVendorPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vendor: string; planName: string; startsOn: string; endsOn?: string; note?: string; supervisionToken: string }) =>
      admin(A.setVendorPlan, { ...input }),
    onSettled: () => invalidateVendors(qc),
  });
}

export interface AllowanceChange {
  vendor: string;
  unit: string;
  included: number;
  warnPct: number;
  stopPct: number;
  paidAllowed: number;
  pricePerUnitUsd?: number;
  supervisionToken?: string;
}

export function useSetVendorAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AllowanceChange) => admin(A.setVendorAllowance, { ...input }),
    onSettled: () => invalidateVendors(qc),
  });
}

export function usePauseVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vendor: string; reason: string }) => admin(A.pauseVendor, { ...input }),
    onSettled: () => invalidateVendors(qc),
  });
}

export function useResumeVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { vendor: string; reason?: string; supervisionToken: string }) => admin(A.resumeVendor, { ...input }),
    onSettled: () => invalidateVendors(qc),
  });
}

// --- Campaigns (ADR-0079, ADR-0086) -------------------------------------------

export function useCampaigns(department: string) {
  const post = useRecordPost();
  return useQuery<{ campaigns: WireCampaign[] }>({
    queryKey: ['campaigns', department],
    queryFn: () => post(P.campaigns, {}),
    // Sends and the heartbeat move the counts; a minute is enough.
    refetchInterval: 60_000,
  });
}

export function useCampaign(campaignId: string | null) {
  const post = useRecordPost();
  return useQuery<WireCampaignDetail>({
    queryKey: ['campaign', campaignId],
    queryFn: () => post(P.campaign, { campaignId }),
    enabled: campaignId !== null,
  });
}

function invalidateCampaigns(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['campaigns'] });
  void qc.invalidateQueries({ queryKey: ['campaign'] });
  void qc.invalidateQueries({ queryKey: ['outreach-queue'] });
}

export interface CampaignTerms {
  name: string;
  templateSubject: string;
  templateBody: string;
  paragraphWords: number;
  markets: string[];
  minTier: 'strong' | 'possible';
  addressKinds: Array<'role' | 'personal'>;
  ceiling: number;
  validFrom: string;
  validTo: string;
}

export function useCreateCampaign() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (terms: CampaignTerms) => post<{ campaign: WireCampaign }>(P.createCampaign, { ...terms }),
    onSettled: () => invalidateCampaigns(qc),
  });
}

/** Spends a `campaign.approve` token bound to `campaign:<id>`; core-api answers it. */
export function useApproveCampaign() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { campaignId: string; fromNameConfirmed: boolean; supervisionToken: string }) =>
      post<{ approved: true; campaign: WireCampaign }>(P.approveCampaign, { ...input }),
    onSettled: () => invalidateCampaigns(qc),
  });
}

export function useEndCampaign() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => post<{ ended: boolean; cancelled: number; campaign: WireCampaign }>(P.endCampaign, { campaignId }),
    onSettled: () => invalidateCampaigns(qc),
  });
}

// --- Mailings a journey may ask for (ADR-0099) --------------------------------

export function useMailings(department: string) {
  const post = useRecordPost();
  return useQuery<{ mailings: WireMailing[]; hook: string }>({
    queryKey: ['mailings', department],
    queryFn: () => post(P.mailings, {}),
    refetchInterval: 60_000,
  });
}

export function useMailing(mailingId: string | null) {
  const post = useRecordPost();
  return useQuery<WireMailingDetail>({
    queryKey: ['mailing', mailingId],
    queryFn: () => post(P.mailing, { mailingId }),
    enabled: mailingId !== null,
  });
}

export function useMailingRequests(mailingId: string | null) {
  const post = useRecordPost();
  return useQuery<{ requests: WireMailingRequest[] }>({
    queryKey: ['mailing-requests', mailingId],
    queryFn: () => post(P.mailingRequests, { mailingId }),
    enabled: mailingId !== null,
    refetchInterval: 60_000,
  });
}

function invalidateMailings(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['mailings'] });
  void qc.invalidateQueries({ queryKey: ['mailing'] });
  void qc.invalidateQueries({ queryKey: ['mailing-requests'] });
}

export interface MailingTerms {
  name: string;
  subject: string;
  body: string;
  lang: string;
  bases: LifecycleBasis[];
  perDayCeiling: number;
  ceiling: number;
  validFrom: string;
  validTo: string;
}

export function useCreateMailing() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (terms: MailingTerms) => post<{ mailing: WireMailing }>(P.createMailing, { ...terms }),
    onSettled: () => invalidateMailings(qc),
  });
}

/** Spends a `mailing.approve` token bound to `mailing:<id>`; core-api answers it. */
export function useApproveMailing() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mailingId: string; fromNameConfirmed: boolean; supervisionToken: string }) =>
      post<{ approved: true; mailing: WireMailing }>(P.approveMailing, { ...input }),
    onSettled: () => invalidateMailings(qc),
  });
}

export function useEndMailing() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mailingId: string) => post<{ ended: boolean; refused: number; mailing: WireMailing }>(P.endMailing, { mailingId }),
    onSettled: () => invalidateMailings(qc),
  });
}

export function useOutreachQueue(department: string) {
  const post = useRecordPost();
  return useQuery<{ queue: WireQueuedTouch[] }>({
    queryKey: ['outreach-queue', department],
    queryFn: () => post(P.outreachQueue, {}),
    refetchInterval: 60_000,
  });
}

/** A person marks a reply as a complaint: its sender goes on the register (ADR-0086). */
export function useMarkComplaint() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => post<{ suppressed: true; added: boolean }>(P.complaint, { messageId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['suppression'] }),
  });
}

/** A tool a client gets at `/v1/mcp`, as `tools/list` names it. */
export interface WireMcpTool {
  name: string;
  title: string;
  annotations: { readOnlyHint: boolean; destructiveHint: boolean };
}

/** Paths no client gets, grouped under the reason they are held back (ADR-0089). */
export interface WireMcpWithheld {
  topic: string;
  reason: string;
  paths: string[];
}

export function useMcpTools() {
  return useQuery({
    queryKey: ['mcp', 'tools'],
    queryFn: () => mcpRead<{ tools: WireMcpTool[] }>('tools/list'),
    staleTime: Infinity,
  });
}

export function useMcpWithheld() {
  return useQuery({
    queryKey: ['mcp', 'withheld'],
    queryFn: () => mcpRead<{ withheld: WireMcpWithheld[] }>('norum/withheld'),
    staleTime: Infinity,
  });
}

/** A Google surface the platform may act on as a person (ADR-0090). */
export type WireDelegationSurface = 'calendar' | 'drive' | 'mail';

export interface WireDelegation {
  surface: WireDelegationSurface;
  grantedBy: string;
  grantedAt: string;
  /** Recorded before the surface came to mean more: it stands for what it meant, and the wider thing waits on recording it again (ADR-0120). */
  predates?: { widenedAt: string; nowIncludes: string };
}

/** A person's standing consents — a superadmin's read, at the admin door. */
export function useDelegations(personId: string, enabled: boolean) {
  return useQuery<{ personId: string; delegations: WireDelegation[] }>({
    queryKey: ['delegations', personId],
    queryFn: () => admin(A.delegations, { personId }),
    enabled,
  });
}

function invalidateDelegations(qc: ReturnType<typeof useQueryClient>, personId: string) {
  void qc.invalidateQueries({ queryKey: ['delegations', personId] });
  void qc.invalidateQueries({ queryKey: ['audit'] });
}

export function useGrantDelegation(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: { surface: WireDelegationSurface; supervisionToken: string }) =>
      admin<{ personId: string; surface: WireDelegationSurface; delegationId: string }>(A.grantDelegation, { personId, ...input }),
    onSettled: () => invalidateDelegations(qc, personId),
  });
}

export function useRevokeDelegation(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (surface: WireDelegationSurface) =>
      admin<{ personId: string; surface: WireDelegationSurface; revoked: true }>(A.revokeDelegation, { personId, surface }),
    onSettled: () => invalidateDelegations(qc, personId),
  });
}

/** A file in one of the department's folders, as Drive describes it (ADR-0091). */
export interface WireDriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string | null;
  lastModifiedBy: string | null;
  link: string | null;
  isFolder: boolean;
}

/**
 * A folder's files, read live as the person under their own drive consent.
 * Asked for on a press, never on page load: each read is a call to Google.
 */
export function useDriveFiles(key: string, companyId: string | undefined, enabled: boolean) {
  const post = useRecordPost();
  return useQuery<{ key: string; path: string; files: WireDriveFile[] }>({
    queryKey: ['drive-files', key, companyId ?? null],
    queryFn: () => post(P.driveFiles, { key, ...(companyId ? { companyId } : {}) }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Where capture stands for one meeting (ADR-0092). */
export interface WireCaptureState {
  eventId: string;
  capture: boolean;
  notesAttached: boolean;
  noteReadAt: string | null;
  noteShredded: boolean;
  noteBlocked: boolean;
}

export interface WireCommitment {
  id: string;
  eventId: string;
  text: string | null;
  state: 'proposed' | 'confirmed' | 'dismissed';
  taskId: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export function useCommitments(eventId: string | null) {
  const post = useRecordPost();
  return useQuery<{ capture: WireCaptureState; commitments: WireCommitment[] }>({
    queryKey: ['commitments', eventId],
    queryFn: () => post(P.commitments, { eventId }),
    enabled: eventId !== null,
  });
}

export function useSetCapture(eventId: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => post<{ eventId: string; capture: boolean; willBeRead?: boolean; why?: string }>(P.capture, { eventId, on }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['commitments', eventId] }),
  });
}

export function useConfirmCommitments(eventId: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => post<{ confirmed: Array<{ commitmentId: string; taskId: string }> }>(P.confirmCommitments, { ids }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['commitments', eventId] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useDismissCommitment(eventId: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ id: string; dismissed: true }>(P.dismissCommitment, { id }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['commitments', eventId] }),
  });
}

// ---------------------------------------------------------------------
// Cover, a person's first days, files on a draft, booking links (ADR-0114)
// ---------------------------------------------------------------------

export interface WireCover {
  id: string;
  covered: { id: string; displayName: string };
  coverer: { id: string; displayName: string };
  startsOn: string;
  endsOn: string;
  setBy: string;
  state: 'upcoming' | 'active' | 'over' | 'ended';
  endedAt: string | null;
  endedBy: string | null;
}

export interface WireCoveringTask {
  id: string;
  title: string;
  state: string;
  department: string | null;
  dueAt: string | null;
  meantFor: { id: string; displayName: string };
}

/** The covers the person is in, those they may arrange, and the tasks a cover gave them. */
export function useCovers() {
  const post = useRecordPost();
  return useQuery<{ mine: WireCover[]; arranged: WireCover[]; tasks: WireCoveringTask[] }>({
    queryKey: ['covers'],
    queryFn: () => post(P.covers, {}),
  });
}

export function useSetCover() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { coveredId: string; covererId: string; startsOn: string; endsOn: string }) => post<{ cover: WireCover }>(P.setCover, input),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['covers'] }),
  });
}

export function useEndCover() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (coverId: string) => post<{ cover: WireCover }>(P.endCover, { coverId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['covers'] }),
  });
}

export interface WireOnboardingStep {
  key: string;
  label: string;
  done: boolean;
  next: string | null;
  optional: boolean;
}

export function useOnboarding(personId: string) {
  const post = useRecordPost();
  return useQuery<{ onboarding: { person: { id: string; displayName: string }; steps: WireOnboardingStep[]; done: number; of: number } }>({
    queryKey: ['onboarding', personId],
    queryFn: () => post(P.onboarding, { personId }),
    retry: false,
  });
}

export interface WireAttachment {
  id: string;
  source: 'drive' | 'upload' | 'inbound';
  filename: string;
  mediaType: string;
  sizeBytes: number | null;
  webLink: string | null;
  expiresAt: string | null;
  addedBy: string;
  addedAt: string;
}

export function useAttachments(draftId: string) {
  const post = useRecordPost();
  return useQuery<{ attachments: WireAttachment[] }>({
    queryKey: ['attachments', draftId],
    queryFn: () => post(P.attachments, { draftId }),
  });
}

function invalidateDraft(qc: ReturnType<typeof useQueryClient>, draftId: string) {
  void qc.invalidateQueries({ queryKey: ['attachments', draftId] });
  void qc.invalidateQueries({ queryKey: ['thread'] });
  void qc.invalidateQueries({ queryKey: ['drafts'] });
}

/** A file from the person's machine, sent as base64; its link is written into the draft's body. */
export function useUploadAttachment(draftId: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { filename: string; mediaType: string; dataBase64: string }) =>
      post<{ attachment: WireAttachment }>(P.uploadAttachment, { draftId, ...input }),
    onSettled: () => invalidateDraft(qc, draftId),
  });
}

/** A file named from the department folder's live listing; its Drive link is written into the body. */
export function useDriveAttachment(draftId: string) {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; fileId: string; companyId?: string }) =>
      post<{ attachment: WireAttachment }>(P.driveAttachment, { draftId, ...input }),
    onSettled: () => invalidateDraft(qc, draftId),
  });
}

export interface WireBookingLink {
  id: string;
  slug: string;
  title: string;
  durationMinutes: number;
  bufferMinutes: number;
  horizonDays: number;
  department: string | null;
  active: boolean;
  createdAt: string;
  held: number;
}

export interface WireBooking {
  id: string;
  linkTitle: string;
  from: string;
  to: string;
  bookedName: string | null;
  bookedEmail: string;
  note: string | null;
  state: 'held' | 'confirmed' | 'cancelled';
  createdAt: string;
}

export function useBookingLinks() {
  const post = useRecordPost();
  return useQuery<{ links: WireBookingLink[]; bookings: WireBooking[]; pageBase: string | null }>({
    queryKey: ['booking-links'],
    queryFn: () => post(P.bookingLinks, {}),
  });
}

export function useCreateBookingLink() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; durationMinutes: number; bufferMinutes?: number; horizonDays?: number }) =>
      post<{ link: WireBookingLink }>(P.createBookingLink, input),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['booking-links'] }),
  });
}

export function useRetireBookingLink() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => post<{ link: WireBookingLink }>(P.retireBookingLink, { linkId }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['booking-links'] }),
  });
}

export function useDecideBooking() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { bookingId: string; decision: 'confirm' | 'cancel' }) =>
      post<{ state: string; onGoogle?: boolean; notOnGoogle?: string }>(P.decideBooking, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['booking-links'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

// --- A twin's task (Amendment 0011, ADR-0139) ------------------------------------

/** Material snapshots for this person's open twin chat. */
export function useChatMaterials(sessionId: string | null) {
  const post = useRecordPost();
  return useQuery<{ materials: WireChatMaterial[] }>({
    queryKey: ['chat-materials', sessionId],
    queryFn: () => post(P.chatMaterialList, { sessionId }),
    enabled: sessionId !== null,
  });
}

export function useUploadChatMaterial() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; filename: string; mediaType: string; dataBase64: string }) =>
      post<{ material: WireChatMaterial }>(P.chatMaterialUpload, input),
    onSuccess: (_value, input) => void qc.invalidateQueries({ queryKey: ['chat-materials', input.sessionId] }),
  });
}

/** Save a Google Doc from the person's visible folder into this twin chat. */
export function useDriveChatMaterial() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; key: string; fileId: string }) =>
      post<{ material: WireChatMaterial }>(P.chatMaterialDrive, input),
    onSuccess: (_value, input) => void qc.invalidateQueries({ queryKey: ['chat-materials', input.sessionId] }),
  });
}

/** Ask the twin for a plan. Nothing runs: the answer is a plan to read, or a handoff to Nia. */
export function useTwinPlan() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId?: string; ask: string; supersedes?: string }) => post<WireTwinPlanAnswer>(P.twinPlan, input),
    onSuccess: (_answer, input) => void qc.invalidateQueries({ queryKey: ['twin-plan-latest', input.sessionId] }),
  });
}

/** Pending plan for a chat, under that chat's owner and twin session. */
export function useLatestTwinPlan(sessionId: string | null) {
  const post = useRecordPost();
  return useQuery<{ plan: Extract<WireTwinPlanAnswer, { kind: 'plan' }> | null }>({
    queryKey: ['twin-plan-latest', sessionId],
    queryFn: () => post(P.twinPlanLatest, { sessionId }),
    enabled: sessionId !== null,
  });
}

/**
 * Confirm a plan: the person's own press, naming the digest of what they
 * read and a key the browser made once for this plan, so a second press or
 * a retry after a dropped answer is the same task, not another.
 */
export function useConfirmTwinTask() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { planId: string; planHash: string; key: string }) => post<WireTwinTaskConfirmed>(P.twinTask, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['twin-tasks'] });
      void qc.invalidateQueries({ queryKey: ['chat'] });
    },
  });
}

/** An unapproved company, asked of Nia in Executive by request (§18, ADR-0064). */
export function useTwinHandoff() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ask: string }) => post<WireTwinHandoff>(P.twinHandoff, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}

export function useTwinTasks() {
  const post = useRecordPost();
  return useQuery<{ tasks: WireTwinTaskRow[] }>({
    queryKey: ['twin-tasks'],
    queryFn: () => post(P.twinTasks, {}),
  });
}

/** A task still running is asked again every so often; one that ended is not. */
const TASK_ENDED = new Set(['done', 'cancelled']);

export function useTwinTask(taskId: string) {
  const post = useRecordPost();
  return useQuery<{ task: WireTwinTask }>({
    queryKey: ['twin-task', taskId],
    queryFn: () => post(P.twinTaskRead, { taskId }),
    refetchInterval: (q) => (q.state.data && TASK_ENDED.has(q.state.data.task.state) ? false : 15_000),
  });
}

/** What twins' tasks wait on, the record of every grant, and where the notices go. */
export function useTaskTools(enabled: boolean) {
  return useQuery<WireTaskTools>({
    queryKey: ['task-tools'],
    queryFn: () => admin(A.taskToolsPending),
    enabled,
    refetchInterval: 60_000,
  });
}

function invalidateTaskTools(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['task-tools'] });
  void qc.invalidateQueries({ queryKey: ['workspace-queue'] });
  void qc.invalidateQueries({ queryKey: ['twin-task'] });
}

/** One tool for one task, under a `task.tool_grant` token bound to the pair. `satisfies` names the integration it answers. */
export function useGrantTaskTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; toolId: string; days: number; satisfies?: string; supervisionToken: string }) =>
      admin<{ taskId: string; toolId: string; days: number; resumed?: boolean }>(A.grantTaskTool, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

/** Refusing narrows, so it spends no token; the task goes back to its person with the reason. */
export function useRefuseTaskTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; toolId: string; reason: string }) =>
      admin<{ taskId: string; toolId: string; returnedTo: string | null }>(A.refuseTaskTool, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

export function useRevokeTaskTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { grantId: string }) => admin<{ grantId: string }>(A.revokeTaskTool, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

/**
 * Stop or start the platform granting a tool on its record in one
 * department. The department is sent as `name`: a body key called
 * `department` is only ever the session's (ADR-0073). Stopping is at once
 * and free; starting again spends a `task.earned_start` token.
 */
export function useEarnedGranting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toolId: string; name: string; state: 'stopped' | 'started'; supervisionToken?: string }) =>
      admin<{ toolId: string; department: string; state: 'stopped' | 'started' }>(A.earnedGranting, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

export function useAddNotifyChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'google_chat' | 'telegram'; target: string; label: string }) =>
      admin<{ id: string; kind: string }>(A.addNotifyChannel, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

export function useRemoveNotifyChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => admin<{ id: string }>(A.removeNotifyChannel, { ...input }),
    onSettled: () => invalidateTaskTools(qc),
  });
}

/**
 * A suggestion for the page in view, to Norum Tech (0132): it is written up
 * as a spec for the superadmin, and followed under Requests.
 */
export function useSuggest() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { route: string; suggestion: string }) =>
      post<{ requestId?: string; taskId?: string; existing?: boolean; waitingOn: string }>(P.suggestionOpen, { ...input }),
    onSettled: () => {
      for (const key of ['requests', 'today', 'workspace-queue']) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

/**
 * A calendar on the person's own list that a hold may go on (ADR-0151): not
 * their own, owned inside the company, and one Google lets them write. The
 * server reads the same rule again on every hold.
 */
export function holdableCalendar(c: WireFollowedCalendar): boolean {
  return !c.primary && !c.external && (c.source ?? 'list') === 'list' && ['writerWithoutPrivateAccess', 'writer', 'owner'].includes(c.accessRole);
}

// ---------------------------------------------------------------------------
// What an assistant proposed (ADR-0153): shortlists, findings, strategies.
// ---------------------------------------------------------------------------

function refreshProposals(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['shortlists'] });
  void qc.invalidateQueries({ queryKey: ['shortlist'] });
  void qc.invalidateQueries({ queryKey: ['evidence-proposals'] });
  void qc.invalidateQueries({ queryKey: ['strategies'] });
}

export function useShortlists(department: string) {
  const post = useRecordPost();
  return useQuery<{ shortlists: WireShortlistRow[]; record: WireAutoconfirmRecord }>({ queryKey: ['shortlists', department], queryFn: () => post(P.shortlists) });
}

/** One shortlist; polled while its businesses are being looked up. */
export function useShortlist(id: string) {
  const post = useRecordPost();
  return useQuery<{ shortlist: WireShortlistFull }>({
    queryKey: ['shortlist', id],
    queryFn: () => post(P.shortlist, { shortlistId: id }),
    enabled: id.length > 0,
    refetchInterval: (q) => (q.state.data?.shortlist.state === 'confirmed' || q.state.data?.shortlist.state === 'resolving' ? 5000 : false),
  });
}

export function useDecideShortlist() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { shortlistId: string; decision: 'confirmed' | 'declined'; planId?: string; drop?: number[] }) =>
      post<{ state: string; jobId: string | null }>(P.decideShortlist, { ...input }),
    onSuccess: () => refreshProposals(qc),
  });
}

export function useEvidenceProposals(department: string, marketCode?: string) {
  const post = useRecordPost();
  return useQuery<{ proposals: WireEvidenceProposal[] }>({
    queryKey: ['evidence-proposals', department, marketCode ?? ''],
    queryFn: () => post(P.evidenceProposals, marketCode ? { marketCode } : {}),
  });
}

export function useDecideEvidence() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { proposalId: string; decision: 'confirmed' | 'declined' }) => post<{ jobId: string | null }>(P.decideEvidence, { ...input }),
    onSuccess: () => {
      refreshProposals(qc);
      void qc.invalidateQueries({ queryKey: ['market'] });
    },
  });
}

export function useStrategies(department: string) {
  const post = useRecordPost();
  return useQuery<{ strategies: WireStrategy[] }>({ queryKey: ['strategies', department], queryFn: () => post(P.strategies) });
}

export function useDecideStrategy() {
  const post = useRecordPost();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { strategyId: string; decision: 'used' | 'template' | 'declined' }) => post<{ decided: string }>(P.decideStrategy, { ...input }),
    onSuccess: () => refreshProposals(qc),
  });
}

/** A superadmin stops a department's shortlists confirming themselves at once, or starts it again under a token. */
export function useAutoconfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; action: 'stop' | 'start'; supervisionToken?: string }) =>
      admin<{ department: string; action: string }>(input.action === 'stop' ? A.autoconfirmStop : A.autoconfirmStart, {
        name: input.name,
        ...(input.supervisionToken ? { supervisionToken: input.supervisionToken } : {}),
      }),
    onSettled: () => refreshProposals(qc),
  });
}

export interface TaskReview { taskId: string; title: string; department: string | null; createdAt: string; progressAt: string; lastPromptedAt: string | null; promptCount: number; dueAt: string | null }
export function useTaskReviews() {
  const post = useRecordPost();
 const me=useContext(MeContext);
 return useQuery<{reviews:TaskReview[]}>({queryKey:['task-reviews',me?.humanId,me?.department],queryFn:()=>post(appPath('/v1/tasks/reviews'),{}),refetchInterval:30_000});
}
export function usePostponeTaskReview() {
  const post = useRecordPost();
 const qc=useQueryClient();return useMutation({mutationFn:(input:{taskId:string;until:string})=>post(appPath('/v1/tasks/reviews/postpone'),input),onSuccess:()=>void qc.invalidateQueries({queryKey:['task-reviews']})});
}

export function useCanonicalRecord(kind:'company'|'contact',id:string,enabled=true) {
 const post=useRecordPost();const me=useContext(MeContext);
 return useQuery({queryKey:['canonical',me?.humanId,kind,id],queryFn:()=>post<CanonicalRecord>(appPath('/v1/duplicates/canonical'),{kind,id}),enabled});
}
export function useConsolidate() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{kind:'company'|'contact';canonicalId:string;sourceId:string;key:string;reason:string;confirmSameIdentity:true;choices:Record<string,string>;descriptions?:Record<string,{mode:'combine'|'reword';text:string;sourceIds:string[]}>})=>post<{event:ConsolidationEvent}>(appPath('/v1/duplicates/consolidate'),input),
 onSuccess:()=>{for(const key of ['canonical','duplicates','company-workspace'])void qc.invalidateQueries({queryKey:[key]});}});
}
export function useReverseConsolidation() {
 const post=useRecordPost();const qc=useQueryClient();
 return useMutation({mutationFn:(input:{eventId:string;reason:string})=>post<{event:ConsolidationEvent}>(appPath('/v1/duplicates/reverse'),input),
 onSuccess:()=>{for(const key of ['canonical','duplicates','company-workspace'])void qc.invalidateQueries({queryKey:[key]});}});
}
